import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis as RawPolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, Cell,
  LineChart, Line,
  ComposedChart,
  ResponsiveContainer,
} from "recharts";
import * as XLSX from "xlsx";
import { useTranslation } from 'react-i18next';

// Recharts v3 type workaround
const PolarAngleAxis = RawPolarAngleAxis as any;
import { supabase } from "./supabase/auth";
import {
  computeFinishedGood,
  buildTotalFormula,
  fdaRound,
  scaleToServing,
  AA_NAMES,
  AA_FULL_NAMES,
  REFERENCE_PATTERN,
  DRV,
  getProteinClaim,
} from "./calculations";
import type {
  CalcIngredient,
  CompositeNutrients,
  PdcaasResult,
} from "./calculations";

const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const MAX_SLOTS = 10;
const DEFAULT_SLOTS = 5;

/* ── Supabase row types ─────────────────────────────────────────────────── */

interface FormulaRow {
  formula_code: string;
  display_name: string;
  piece_weight_g: number;
  serving_size_g: number;
  dough_ratio_pct: number;
  single_component: boolean | null;
  ratio_mode: string | null;
  final_moisture_mode: string | null;
  final_moisture_whole: number | null;
  final_moisture_dough: number | null;
  final_moisture_filling: number | null;
}

interface ComponentRow {
  formula_code: string;
  component: string;
  ingredient_id: string;
  pct_of_component: number;
  row_type: string | null;
}

interface IngredientRow {
  id: string;
  common_name: string;
  is_active: boolean | null;
  protein_per_100g: number;
  digestibility: number;
  cost_per_kg: number;
  fat: number;
  satfat: number;
  transfat: number;
  cholesterol: number;
  carbs: number;
  fiber: number;
  sugar: number;
  sodium: number;
  moisture: number;
  vitamin_d: number;
  calcium: number;
  iron: number;
  potassium: number;
  color: string;
  aa_his: number;
  aa_ile: number;
  aa_leu: number;
  aa_lys: number;
  aa_mc: number;
  aa_pt: number;
  aa_thr: number;
  aa_trp: number;
  aa_val: number;
  fa_ala: number | null;
  fa_la: number | null;
  fa_oleic: number | null;
  fa_palmitic: number | null;
  fa_stearic: number | null;
  fiber_insoluble: number | null;
  fiber_soluble: number | null;
  resistant_starch: number | null;
  korean_name: string | null;
}

/* ── Map Supabase ingredient row → CalcIngredient ─────────────────────── */

function toCalcIngredient(r: IngredientRow): CalcIngredient {
  return {
    protein_per_100g: r.protein_per_100g,
    digestibility: r.digestibility,
    cost_per_kg: r.cost_per_kg,
    fat: r.fat,
    satfat: r.satfat,
    transfat: r.transfat,
    cholesterol: r.cholesterol,
    carbs: r.carbs,
    fiber: r.fiber,
    sugar: r.sugar,
    sodium: r.sodium,
    moisture: r.moisture,
    vitaminD: r.vitamin_d,
    calcium: r.calcium,
    iron: r.iron,
    potassium: r.potassium,
    color: r.color || "#888888",
    amino_acids: {
      histidine: r.aa_his,
      isoleucine: r.aa_ile,
      leucine: r.aa_leu,
      lysine: r.aa_lys,
      methionine_cysteine: r.aa_mc,
      phenylalanine_tyrosine: r.aa_pt,
      threonine: r.aa_thr,
      tryptophan: r.aa_trp,
      valine: r.aa_val,
    },
    fa_ala: r.fa_ala ?? 0,
    fa_la: r.fa_la ?? 0,
    fa_oleic: r.fa_oleic ?? 0,
    fa_palmitic: r.fa_palmitic ?? 0,
    fa_stearic: r.fa_stearic ?? 0,
    fiber_insoluble: r.fiber_insoluble ?? 0,
    fiber_soluble: r.fiber_soluble ?? 0,
    resistant_starch: r.resistant_starch ?? 0,
  };
}

/* ── Loaded formula (raw from Supabase) ───────────────────────────────── */

interface LoadedFormula {
  formula: FormulaRow;
  doughFormula: Record<string, number>;
  fillingFormula: Record<string, number>;
}

/* ── Computed formula (all derived values for one column) ─────────────── */

export interface DerivedMetrics {
  formula_code: string;
  display_name: string;
  calories_per_100g: number;
  protein_per_100g: number;
  fiber_per_100g: number;
  soluble_fiber_per_100g: number;
  insoluble_fiber_per_100g: number;
  resistant_starch_per_100g: number;
  corrected_protein_per_100g: number;
  protein_per_100kcal: number;
  fiber_per_100kcal: number;
  soluble_fiber_per_100kcal: number;
  corrected_protein_per_100kcal: number;
  protein_plus_fiber_per_100kcal: number;
  corrected_protein_plus_fiber_per_100kcal: number;
  calories_minus_fiber4: number;
  corrected_protein_per_net_kcal: number;
  ala_per_100g: number;
  oleic_per_100g: number;
  la_per_100g: number;
  satfat_per_100g: number;
  healthy_fat_signal: number;
  n6_n3_ratio: number;
  sodium_per_100g: number;
  sodium_per_100kcal: number;
  digestibility: number;
  amino_acid_score: number;
  pdcaas: number;
  iron_per_100kcal: number;
  calcium_per_100kcal: number;
  potassium_per_100kcal: number;
  // Satiety-focused metrics (net kcal = calories - fiber*4)
  protein_per_net_kcal: number;
  fiber_per_net_kcal: number;
  soluble_fiber_per_net_kcal: number;
  protein_plus_fiber_per_net_kcal: number;
  corrected_protein_plus_fiber_per_net_kcal: number;
  iron_per_net_kcal: number;
  calcium_per_net_kcal: number;
  potassium_per_net_kcal: number;
  oleic_per_net_kcal: number;
  ala_per_net_kcal: number;
  leucine_mg_per_g_protein: number;
  lysine_mg_per_g_protein: number;
  protein_quality_gap_pct: number;
  satiety_composite: number;
  // Ingredient composition breakdown
  ingredient_breakdown: {
    name: string;
    pct: number;
    beneficial: boolean;
    category: string;
  }[];
  pct_beneficial_ingredients: number;
  pct_protein_sources: number;
  pct_fiber_sources: number;
  pct_healthy_fat_sources: number;
}

// Classify ingredients by nutritional role
function classifyIngredient(
  name: string,
  ing: CalcIngredient | undefined
): { beneficial: boolean; category: string } {
  if (!ing) return { beneficial: false, category: "unknown" };
  const lc = name.toLowerCase();

  // Protein sources: >15% protein
  if (ing.protein_per_100g > 15) {
    return { beneficial: true, category: "protein_source" };
  }
  // Fiber sources: >5% fiber
  if (ing.fiber > 5 || (ing.fiber_soluble ?? 0) > 2 || (ing.resistant_starch ?? 0) > 2) {
    return { beneficial: true, category: "fiber_source" };
  }
  // Healthy fat sources: high oleic or ALA, low satfat ratio
  if (
    (ing.fa_oleic ?? 0) > 5 ||
    (ing.fa_ala ?? 0) > 1 ||
    (lc.includes("flax") || lc.includes("chia") || lc.includes("olive"))
  ) {
    return { beneficial: true, category: "healthy_fat" };
  }
  // Micronutrient-dense: iron, calcium, potassium rich
  if (ing.iron > 3 || ing.calcium > 100 || ing.potassium > 300) {
    return { beneficial: true, category: "micronutrient_dense" };
  }
  // Filler/binder/sugar/sodium-heavy
  if (ing.sugar > 30 || ing.sodium > 500 || (ing.fat > 20 && (ing.fa_oleic ?? 0) < 5)) {
    return { beneficial: false, category: "filler_or_burden" };
  }
  // Water/moisture
  if (lc.includes("water") || ing.moisture > 90) {
    return { beneficial: false, category: "water" };
  }
  return { beneficial: false, category: "other" };
}

function buildDerivedMetrics(
  c: ComputedFormula,
  ingredientMap: Record<string, CalcIngredient>
): DerivedMetrics {
  const n = c.nutrients;
  const cal = n.calories;
  const per100kcal = (v: number) => (cal > 0 ? (v / cal) * 100 : 0);
  const netKcal = Math.max(cal - n.fiber * 4, 1);
  const perNetKcal = (v: number) => (v / netKcal) * 100;

  // Classify each ingredient
  const breakdown = c.ingredientList.map((ing) => {
    const ingData = ingredientMap[ing.name];
    const { beneficial, category } = classifyIngredient(ing.name, ingData);
    return { name: ing.name, pct: ing.pct, beneficial, category };
  });

  const totalPct = breakdown.reduce((s, b) => s + b.pct, 0) || 1;
  const beneficialPct = breakdown
    .filter((b) => b.beneficial)
    .reduce((s, b) => s + b.pct, 0);
  const proteinPct = breakdown
    .filter((b) => b.category === "protein_source")
    .reduce((s, b) => s + b.pct, 0);
  const fiberPct = breakdown
    .filter((b) => b.category === "fiber_source")
    .reduce((s, b) => s + b.pct, 0);
  const healthyFatPct = breakdown
    .filter((b) => b.category === "healthy_fat")
    .reduce((s, b) => s + b.pct, 0);

  return {
    formula_code: c.code,
    display_name: c.displayName,
    calories_per_100g: cal,
    protein_per_100g: n.protein,
    fiber_per_100g: n.fiber,
    soluble_fiber_per_100g: n.fiberSoluble,
    insoluble_fiber_per_100g: n.fiberInsoluble,
    resistant_starch_per_100g: n.resistantStarch,
    corrected_protein_per_100g: c.pdcaas.correctedProteinPer100g,
    protein_per_100kcal: per100kcal(n.protein),
    fiber_per_100kcal: per100kcal(n.fiber),
    soluble_fiber_per_100kcal: per100kcal(n.fiberSoluble),
    corrected_protein_per_100kcal: per100kcal(c.pdcaas.correctedProteinPer100g),
    protein_plus_fiber_per_100kcal: per100kcal(n.protein + n.fiber),
    corrected_protein_plus_fiber_per_100kcal: per100kcal(
      c.pdcaas.correctedProteinPer100g + n.fiber
    ),
    calories_minus_fiber4: cal - n.fiber * 4,
    corrected_protein_per_net_kcal:
      (c.pdcaas.correctedProteinPer100g / netKcal) * 100,
    ala_per_100g: n.faAla,
    oleic_per_100g: n.faOleic,
    la_per_100g: n.faLa,
    satfat_per_100g: n.satfat,
    // healthy_fat_signal: ALA*10 + oleic - satfat (ALA weighted ×10 for scale)
    healthy_fat_signal: n.faAla * 10 + n.faOleic - n.satfat,
    n6_n3_ratio: n.faAla > 0 ? n.faLa / n.faAla : 0,
    sodium_per_100g: n.sodium,
    sodium_per_100kcal: per100kcal(n.sodium),
    digestibility: c.pdcaas.weightedDigestibility,
    amino_acid_score: c.pdcaas.aminoAcidScore,
    pdcaas: c.pdcaas.truncatedPdcaas,
    iron_per_100kcal: per100kcal(n.iron),
    calcium_per_100kcal: per100kcal(n.calcium),
    potassium_per_100kcal: per100kcal(n.potassium),
    // Satiety-focused: all per net kcal (calories - fiber*4)
    protein_per_net_kcal: perNetKcal(n.protein),
    fiber_per_net_kcal: perNetKcal(n.fiber),
    soluble_fiber_per_net_kcal: perNetKcal(n.fiberSoluble),
    protein_plus_fiber_per_net_kcal: perNetKcal(n.protein + n.fiber),
    corrected_protein_plus_fiber_per_net_kcal: perNetKcal(
      c.pdcaas.correctedProteinPer100g + n.fiber
    ),
    iron_per_net_kcal: perNetKcal(n.iron),
    calcium_per_net_kcal: perNetKcal(n.calcium),
    potassium_per_net_kcal: perNetKcal(n.potassium),
    oleic_per_net_kcal: perNetKcal(n.faOleic),
    ala_per_net_kcal: perNetKcal(n.faAla),
    leucine_mg_per_g_protein: c.pdcaas.blendedAA?.leucine ?? 0,
    lysine_mg_per_g_protein: c.pdcaas.blendedAA?.lysine ?? 0,
    // Gap between total and corrected protein (% lost to quality/digestibility)
    protein_quality_gap_pct:
      n.protein > 0
        ? ((n.protein - c.pdcaas.correctedProteinPer100g) / n.protein) * 100
        : 0,
    // Satiety composite: protein + soluble fiber + resistant starch + oleic, all per net kcal
    satiety_composite:
      perNetKcal(n.protein) +
      perNetKcal(n.fiberSoluble) * 2 +
      perNetKcal(n.resistantStarch) * 1.5 +
      perNetKcal(n.faOleic) * 0.5,
    // Ingredient composition analysis
    ingredient_breakdown: breakdown,
    pct_beneficial_ingredients: (beneficialPct / totalPct) * 100,
    pct_protein_sources: (proteinPct / totalPct) * 100,
    pct_fiber_sources: (fiberPct / totalPct) * 100,
    pct_healthy_fat_sources: (healthyFatPct / totalPct) * 100,
  };
}

interface ComputedFormula {
  code: string;
  displayName: string;
  formula: FormulaRow;
  nutrients: CompositeNutrients;
  pdcaas: PdcaasResult;
  effectiveDoughRatio: number;
  effectiveFillingRatio: number;
  isPostProcessed: boolean;
  ingredientList: { name: string; pct: number }[];
}

/* ── Formatting helpers ───────────────────────────────────────────────── */

function fmt(v: number, dp: number = 2): string {
  return v.toFixed(dp);
}

function fmtFda(v: number | string): string {
  if (typeof v === "string") return v;
  return String(v);
}

/* ── Table style constants ────────────────────────────────────────────── */

const LABEL_WIDTH = 250;
const COL_WIDTH = 160;

const cellBase: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: "13px",
  borderBottom: "1px solid rgba(139,92,246,0.1)",
  verticalAlign: "top",
  whiteSpace: "nowrap",
};

const labelCell: React.CSSProperties = {
  ...cellBase,
  position: "sticky",
  left: 0,
  background: "#0f0b2a",
  zIndex: 2,
  width: LABEL_WIDTH,
  minWidth: LABEL_WIDTH,
  maxWidth: LABEL_WIDTH,
  color: "#cbd5e1",
  fontWeight: 500,
};

const dataCell: React.CSSProperties = {
  ...cellBase,
  width: COL_WIDTH,
  minWidth: COL_WIDTH,
  textAlign: "right",
  color: "#f8fafc",
};

const sectionHeaderStyle: React.CSSProperties = {
  padding: "10px 10px 6px",
  fontSize: "13px",
  fontWeight: 700,
  color: "#a78bfa",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  borderBottom: "2px solid rgba(139,92,246,0.3)",
  background: "#0f0b2a",
  position: "sticky",
  left: 0,
  zIndex: 2,
};

/* ── PDCAAS color ─────────────────────────────────────────────────────── */

function pdcaasColor(val: number): string {
  if (val >= 0.8) return "#22c55e";
  if (val >= 0.6) return "#f59e0b";
  return "#ef4444";
}

function claimColor(claim: string): string {
  if (claim === "Excellent Source") return "#22c55e";
  if (claim === "Good Source") return "#f59e0b";
  return "#64748b";
}

/* ── Gauge / Speedometer helpers ───────────────────────────────────────── */

const GAUGE_START_ANGLE = 135; // degrees (bottom-left)
const GAUGE_END_ANGLE = 405;  // degrees (bottom-right, 270° sweep)
const GAUGE_SWEEP = GAUGE_END_ANGLE - GAUGE_START_ANGLE;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

/* ── Animated gauge speedometer ────────────────────────────────────────── */

function GaugeSpeedometer({
  value,
  target,
  maxVal,
  label,
  unit,
  color,
  size = 160,
  animDelay = 0,
  t,
}: {
  value: number;
  target: number;
  maxVal: number;
  label: string;
  unit: string;
  t: (key: string) => string;
  color: string;
  size?: number;
  animDelay?: number;
}) {
  const cx = size / 2;
  const cy = size * 0.52;
  const r = size * 0.38;
  const strokeW = size * 0.055;
  const tickR = r + strokeW / 2 + 4;
  const innerTickR = r - strokeW / 2 - 2;

  const overTarget = value > target;
  // Allow needle to swing past max when over target (up to 120% of maxVal)
  const clampedPct = overTarget ? Math.min(value / maxVal, 1.15) : Math.min(value / maxVal, 1);
  const targetPct = Math.min(target / maxVal, 1);
  const needleAngle = GAUGE_START_ANGLE + clampedPct * GAUGE_SWEEP;
  const targetAngle = GAUGE_START_ANGLE + targetPct * GAUGE_SWEEP;

  // Tick marks: major every 20%, minor every 10%
  const ticks: { angle: number; major: boolean; labelVal: string }[] = [];
  for (let i = 0; i <= 10; i++) {
    const pct = i / 10;
    const angle = GAUGE_START_ANGLE + pct * GAUGE_SWEEP;
    const isMajor = i % 2 === 0;
    ticks.push({ angle, major: isMajor, labelVal: isMajor ? `${Math.round(pct * maxVal)}` : "" });
  }

  // Danger zone: from target to maxVal
  const dangerStart = targetAngle;
  const dangerEnd = GAUGE_END_ANGLE;

  // Needle tip
  const needleTip = polarToCartesian(cx, cy, r - 6, needleAngle);
  const needleBase1 = polarToCartesian(cx, cy, 6, needleAngle - 90);
  const needleBase2 = polarToCartesian(cx, cy, 6, needleAngle + 90);

  // "Broken" crack lines when over target
  const cracks = overTarget ? [
    { x1: cx + r * 0.15, y1: cy - r * 0.35, x2: cx + r * 0.30, y2: cy - r * 0.55 },
    { x1: cx + r * 0.30, y1: cy - r * 0.55, x2: cx + r * 0.22, y2: cy - r * 0.70 },
    { x1: cx + r * 0.30, y1: cy - r * 0.55, x2: cx + r * 0.45, y2: cy - r * 0.60 },
    { x1: cx - r * 0.10, y1: cy - r * 0.50, x2: cx - r * 0.05, y2: cy - r * 0.72 },
    { x1: cx - r * 0.05, y1: cy - r * 0.72, x2: cx + r * 0.08, y2: cy - r * 0.78 },
    { x1: cx - r * 0.05, y1: cy - r * 0.72, x2: cx - r * 0.18, y2: cy - r * 0.80 },
  ] : [];

  const sparkId = `speedo-spark-${label.replace(/\s/g, "")}`;

  return (
    <div style={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      animation: `gaugeSlideUp 0.6s ease-out ${animDelay}s both`,
      position: "relative",
    }}>
      <svg width={size} height={size * 0.72} viewBox={`0 0 ${size} ${size * 0.72}`}>
        <defs>
          <filter id={`glow-${label.replace(/\s/g, "")}`}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={`${sparkId}-glow`}>
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id={`grad-${label.replace(/\s/g, "")}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="1" />
          </linearGradient>
          {/* Danger zone gradient */}
          <linearGradient id={`danger-${label.replace(/\s/g, "")}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.9" />
          </linearGradient>
        </defs>

        {/* Outer bezel circle */}
        <circle cx={cx} cy={cy} r={r + strokeW / 2 + 8} fill="none"
          stroke="rgba(139,92,246,0.15)" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r={r + strokeW / 2 + 10} fill="none"
          stroke="rgba(139,92,246,0.06)" strokeWidth="1" />

        {/* Background arc */}
        <path
          d={describeArc(cx, cy, r, GAUGE_START_ANGLE, GAUGE_END_ANGLE)}
          fill="none"
          stroke="rgba(139,92,246,0.08)"
          strokeWidth={strokeW}
          strokeLinecap="round"
        />

        {/* Danger zone arc (target to max) */}
        <path
          d={describeArc(cx, cy, r, dangerStart, dangerEnd)}
          fill="none"
          stroke={`url(#danger-${label.replace(/\s/g, "")})`}
          strokeWidth={strokeW}
          strokeLinecap="round"
          opacity="0.4"
        />

        {/* Value arc */}
        {(() => {
          const circumference = (GAUGE_SWEEP / 360) * 2 * Math.PI * r;
          const valueDash = Math.min(clampedPct, 1) * circumference;
          return (
            <path
              d={describeArc(cx, cy, r, GAUGE_START_ANGLE, GAUGE_END_ANGLE)}
              fill="none"
              stroke={overTarget ? "#ef4444" : `url(#grad-${label.replace(/\s/g, "")})`}
              strokeWidth={strokeW}
              strokeLinecap="round"
              strokeDasharray={`${circumference}`}
              strokeDashoffset={circumference - valueDash}
              filter={`url(#glow-${label.replace(/\s/g, "")})`}
              style={{
                animation: `gaugeFill 1.2s ease-out ${animDelay + 0.3}s both`,
                ["--gauge-dash" as any]: `${circumference - valueDash}`,
                ["--gauge-full" as any]: `${circumference}`,
              }}
            />
          );
        })()}

        {/* Tick marks */}
        {ticks.map((tick, i) => {
          const outerPt = polarToCartesian(cx, cy, tickR, tick.angle);
          const innerPt = polarToCartesian(cx, cy, tick.major ? innerTickR : innerTickR + 4, tick.angle);
          const labelPt = polarToCartesian(cx, cy, tickR + 8, tick.angle);
          const isInDanger = tick.angle >= dangerStart;
          return (
            <g key={i}>
              <line
                x1={outerPt.x} y1={outerPt.y}
                x2={innerPt.x} y2={innerPt.y}
                stroke={isInDanger ? "rgba(239,68,68,0.5)" : "rgba(148,163,184,0.4)"}
                strokeWidth={tick.major ? 2 : 1}
                strokeLinecap="round"
              />
              {tick.major && (
                <text x={labelPt.x} y={labelPt.y} textAnchor="middle" dominantBaseline="middle"
                  fontSize="7" fill={isInDanger ? "rgba(239,68,68,0.7)" : "#64748b"} fontWeight="600">
                  {tick.labelVal}
                </text>
              )}
            </g>
          );
        })}

        {/* Target marker line (thicker) */}
        {(() => {
          const tOuter = polarToCartesian(cx, cy, tickR + 2, targetAngle);
          const tInner = polarToCartesian(cx, cy, innerTickR - 4, targetAngle);
          return (
            <line x1={tOuter.x} y1={tOuter.y} x2={tInner.x} y2={tInner.y}
              stroke="#f8fafc" strokeWidth={2.5} strokeLinecap="round" opacity="0.8" />
          );
        })()}

        {/* Needle */}
        <polygon
          points={`${needleTip.x},${needleTip.y} ${needleBase1.x},${needleBase1.y} ${needleBase2.x},${needleBase2.y}`}
          fill={overTarget ? "#ef4444" : color}
          filter={overTarget ? `url(#${sparkId}-glow)` : undefined}
          opacity={0.95}
          style={{
            animation: `needleSwing 1s ease-out ${animDelay + 0.3}s both`,
            transformOrigin: `${cx}px ${cy}px`,
          }}
        />
        {/* Needle center hub */}
        <circle cx={cx} cy={cy} r={5} fill={overTarget ? "#ef4444" : color}
          stroke="rgba(15,11,42,0.8)" strokeWidth={2} />
        <circle cx={cx} cy={cy} r={2.5} fill="#f8fafc" opacity="0.6" />

        {/* Broken glass cracks when over target */}
        {cracks.map((c, i) => (
          <line key={i} x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2}
            stroke="rgba(239,68,68,0.6)" strokeWidth="1.2" strokeLinecap="round"
            style={{
              opacity: 0,
              animation: `crackAppear 0.3s ease-out ${animDelay + 1.2 + i * 0.05}s forwards`,
            }}
          />
        ))}

        {/* Spark particles when broken */}
        {overTarget && [0, 1, 2, 3, 4].map(i => {
          const sparkAngle = needleAngle + (i - 2) * 15;
          const sp = polarToCartesian(cx, cy, r - 10, sparkAngle);
          return (
            <circle key={i} cx={sp.x} cy={sp.y} r="1.5"
              fill="#fbbf24" filter={`url(#${sparkId}-glow)`}
              style={{
                opacity: 0,
                animation: `sparkFly 0.8s ease-out ${animDelay + 1.3 + i * 0.1}s forwards`,
                transformOrigin: `${sp.x}px ${sp.y}px`,
              }}
            />
          );
        })}

        {/* Center value */}
        <text x={cx} y={cy + r * 0.45} textAnchor="middle"
          fontSize={size * 0.15} fontWeight="800"
          fill={overTarget ? "#ef4444" : (value >= target ? "#22c55e" : color)}
          style={overTarget ? { animation: `brokenShake 0.5s ease-in-out ${animDelay + 1.2}s both` } : {}}>
          {value.toFixed(1)}
        </text>
        <text x={cx} y={cy + r * 0.45 + 13} textAnchor="middle" fontSize="9" fill="#94a3b8">
          {unit} / {t('compare.servingUnit')}
        </text>

        {/* Over target badge */}
        {overTarget && (
          <text x={cx} y={cy + r * 0.45 + 26} textAnchor="middle" fontSize="8" fill="#ef4444" fontWeight="700"
            style={{ animation: `gaugePulse 1.5s ease-in-out infinite ${animDelay + 1.5}s` }}>
            {t('compare.overTarget')}
          </text>
        )}
        {/* Hit target badge */}
        {value >= target && !overTarget && (
          <text x={cx} y={cy + r * 0.45 + 26} textAnchor="middle" fontSize="8" fill="#22c55e" fontWeight="700"
            style={{ animation: `gaugePulse 2s ease-in-out infinite ${animDelay + 1}s` }}>
            {t('compare.targetHit')}
          </text>
        )}
      </svg>
      <div style={{ fontSize: "11px", color: "#c4b5fd", fontWeight: 600, marginTop: "-6px", letterSpacing: "0.5px" }}>
        {label}
      </div>
    </div>
  );
}

/* ── Nested %DV ring gauge ─────────────────────────────────────────────── */

function NestedDVGauge({
  proteinDV,
  fiberDV,
  fatDV,
  formulaCode,
  size = 170,
  animDelay = 0,
  t,
}: {
  proteinDV: number;
  fiberDV: number;
  fatDV: number;
  formulaCode: string;
  size?: number;
  animDelay?: number;
  t: (key: string) => string;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const rings = [
    { label: t('formulation.protein'), pct: Math.min(proteinDV, 150), color: "#8b5cf6", r: size * 0.40, strokeW: 10 },
    { label: t('formulation.fiber'), pct: Math.min(fiberDV, 150), color: "#22c55e", r: size * 0.30, strokeW: 10 },
    { label: t('formulation.fat'), pct: Math.min(fatDV, 150), color: "#f59e0b", r: size * 0.20, strokeW: 10 },
  ];

  return (
    <div style={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      animation: `gaugeSlideUp 0.6s ease-out ${animDelay}s both`,
    }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <filter id={`dvglow-${formulaCode}`}>
            <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {rings.map((ring, i) => {
          const fullCirc = 2 * Math.PI * ring.r;
          const valueDash = (Math.min(ring.pct, 100) / 100) * fullCirc;
          return (
            <g key={ring.label}>
              {/* Background ring */}
              <circle cx={cx} cy={cy} r={ring.r} fill="none" stroke="rgba(139,92,246,0.08)" strokeWidth={ring.strokeW} />
              {/* Value ring */}
              <circle
                cx={cx} cy={cy} r={ring.r}
                fill="none"
                stroke={ring.color}
                strokeWidth={ring.strokeW}
                strokeLinecap="round"
                strokeDasharray={`${fullCirc}`}
                strokeDashoffset={fullCirc - valueDash}
                transform={`rotate(-90 ${cx} ${cy})`}
                filter={`url(#dvglow-${formulaCode})`}
                opacity="0.85"
                style={{
                  animation: `gaugeFillRing 1s ease-out ${animDelay + 0.2 + i * 0.15}s both`,
                  ["--ring-dash" as any]: `${fullCirc - valueDash}`,
                  ["--ring-full" as any]: `${fullCirc}`,
                }}
              />
              {/* % label at end of arc */}
              <text
                x={cx + ring.r + 8}
                y={cy - ring.r + ring.strokeW + 2 + i * 0}
                fontSize="9"
                fill={ring.color}
                fontWeight="700"
                textAnchor="start"
                dominantBaseline="middle"
                style={{ opacity: 0, animation: `gaugeFadeIn 0.5s ease-out ${animDelay + 0.8 + i * 0.15}s forwards` }}
              >
                {ring.pct.toFixed(0)}%
              </text>
            </g>
          );
        })}
        {/* Center label */}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="11" fill="#f8fafc" fontWeight="700">{t('compare.pctDVShort')}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="8" fill="#64748b">{t('compare.perServingShort')}</text>
      </svg>
      {/* Legend below */}
      <div style={{ display: "flex", gap: "10px", marginTop: "-2px" }}>
        {rings.map((ring) => (
          <div key={ring.label} style={{ display: "flex", alignItems: "center", gap: "3px" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: ring.color }} />
            <span style={{ fontSize: "9px", color: "#94a3b8" }}>{ring.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Nutrient Stacked Bar Chart ─────────────────────────────────────────── */

function NutrientStackedBar({
  formulas,
  currentIndex,
  animDelay = 0,
  t,
}: {
  formulas: { code: string; protein: number; fiber: number; micro: number; other: number }[];
  currentIndex: number;
  animDelay?: number;
  t: (key: string) => string;
}) {
  const barWidth = 44;
  const barGap = 16;
  const maxHeight = 140;
  const labelH = 28;
  const topPad = 16;

  const maxTotal = Math.max(...formulas.map(f => f.protein + f.fiber + f.micro), 0.01);

  const chartWidth = Math.max(formulas.length * (barWidth + barGap) + barGap + 40, 200);
  const chartHeight = maxHeight + labelH + topPad + 20;

  const segments: { key: string; color: string; glow: boolean }[] = [
    { key: "protein", color: "#8b5cf6", glow: true },
    { key: "fiber", color: "#22c55e", glow: true },
    { key: "micro", color: "#06b6d4", glow: true },
  ];

  return (
    <div style={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      animation: `gaugeSlideUp 0.7s ease-out ${animDelay}s both`,
      width: "100%",
    }}>
      <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        <defs>
          <filter id="bar-glow-purple">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feFlood floodColor="#8b5cf6" floodOpacity="0.5" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="bar-glow-green">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feFlood floodColor="#22c55e" floodOpacity="0.5" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="bar-glow-cyan">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feFlood floodColor="#06b6d4" floodOpacity="0.5" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge><feMergeNode in="glow" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Horizontal grid lines */}
        {[0.25, 0.5, 0.75, 1].map(pct => {
          const yy = topPad + maxHeight * (1 - pct);
          return (
            <g key={pct}>
              <line x1={28} y1={yy} x2={chartWidth - 10} y2={yy}
                stroke="rgba(139,92,246,0.08)" strokeWidth="1" strokeDasharray="4,4" />
              <text x={24} y={yy + 3} textAnchor="end" fontSize="7" fill="#64748b">
                {(maxTotal * pct).toFixed(0)}
              </text>
            </g>
          );
        })}

        {/* Stacked bars */}
        {formulas.map((f, fIdx) => {
          const total = f.protein + f.fiber + f.micro;
          const barX = 36 + fIdx * (barWidth + barGap);
          const isCurrent = fIdx === currentIndex;

          let yOffset = topPad + maxHeight; // bottom of bar area
          const vals = [f.protein, f.fiber, f.micro];

          return (
            <g key={f.code}>
              {/* Bar segments (bottom to top: other, micro, fiber, protein) */}
              {[...vals].reverse().map((val, revIdx) => {
                const segIdx = vals.length - 1 - revIdx; // original index
                const seg = segments[segIdx];
                const segH = (val / maxTotal) * maxHeight;
                yOffset -= segH;
                const glowFilter = seg.glow && isCurrent
                  ? seg.key === "protein" ? "url(#bar-glow-purple)"
                    : seg.key === "fiber" ? "url(#bar-glow-green)"
                    : "url(#bar-glow-cyan)"
                  : undefined;
                return (
                  <rect key={seg.key}
                    x={barX} y={yOffset}
                    width={barWidth} height={Math.max(segH, 0)}
                    rx={revIdx === 0 ? 4 : (segIdx === 0 && segH > 0) ? 4 : 0}
                    fill={seg.glow && isCurrent ? seg.color : (seg.glow ? seg.color + "55" : seg.color)}
                    filter={glowFilter}
                    opacity={isCurrent ? 1 : 0.5}
                    style={{
                      animation: `barGrow 0.8s ease-out ${animDelay + 0.3 + fIdx * 0.1}s both`,
                      transformOrigin: `${barX + barWidth / 2}px ${topPad + maxHeight}px`,
                    }}
                  />
                );
              })}
              {/* Value on top of bar */}
              <text x={barX + barWidth / 2} y={topPad + maxHeight - (total / maxTotal) * maxHeight - 4}
                textAnchor="middle" fontSize="8" fontWeight="700"
                fill={isCurrent ? "#f8fafc" : "#64748b"}
                style={{ opacity: 0, animation: `gaugeFadeIn 0.4s ease-out ${animDelay + 0.9 + fIdx * 0.1}s forwards` }}>
                {total.toFixed(0)}
              </text>
              {/* Formula label */}
              <text x={barX + barWidth / 2} y={topPad + maxHeight + 14}
                textAnchor="middle" fontSize="9" fontWeight={isCurrent ? "700" : "500"}
                fill={isCurrent ? "#c4b5fd" : "#64748b"}>
                {f.code.length > 6 ? f.code.slice(0, 6) + ".." : f.code}
              </text>
              {/* Current indicator dot */}
              {isCurrent && (
                <circle cx={barX + barWidth / 2} cy={topPad + maxHeight + 22} r={2.5}
                  fill="#8b5cf6" style={{ animation: `gaugePulse 2s ease-in-out infinite ${animDelay + 1}s` }} />
              )}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center", marginTop: "2px" }}>
        {segments.map(s => (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div style={{
              width: 10, height: 10, borderRadius: "3px", background: s.color,
              boxShadow: `0 0 6px ${s.color}`,
            }} />
            <span style={{ fontSize: "9px", color: "#c4b5fd", fontWeight: 600, textTransform: "capitalize" }}>
              {s.key === "micro" ? t('compare.micronutrients') : s.key}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Keyframe styles (injected once) ──────────────────────────────────── */

const GAUGE_KEYFRAMES = `
@keyframes gaugeSlideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes gaugeFill {
  from { stroke-dashoffset: var(--gauge-full); }
  to { stroke-dashoffset: var(--gauge-dash); }
}
@keyframes gaugeFillRing {
  from { stroke-dashoffset: var(--ring-full); }
  to { stroke-dashoffset: var(--ring-dash); }
}
@keyframes gaugeFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes gaugePulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
@keyframes auraPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.04); opacity: 0.7; }
}
@keyframes dashboardGlow {
  0%, 100% { box-shadow: 0 0 20px rgba(139,92,246,0.1), inset 0 0 20px rgba(139,92,246,0.03); }
  50% { box-shadow: 0 0 30px rgba(139,92,246,0.2), inset 0 0 30px rgba(139,92,246,0.06); }
}
@keyframes needleSwing {
  0% { transform: rotate(-135deg); opacity: 0; }
  60% { opacity: 1; }
  100% { transform: rotate(0deg); opacity: 1; }
}
@keyframes crackAppear {
  from { opacity: 0; stroke-dashoffset: 20; }
  to { opacity: 1; stroke-dashoffset: 0; }
}
@keyframes sparkFly {
  0% { opacity: 0; r: 1; }
  30% { opacity: 1; r: 2.5; }
  100% { opacity: 0; r: 0.5; transform: translateY(-8px); }
}
@keyframes brokenShake {
  0%, 100% { transform: translateX(0); }
  15% { transform: translateX(-3px); }
  30% { transform: translateX(3px); }
  45% { transform: translateX(-2px); }
  60% { transform: translateX(2px); }
  75% { transform: translateX(-1px); }
  90% { transform: translateX(1px); }
}
@keyframes barGrow {
  from { transform: scaleY(0); opacity: 0; }
  to { transform: scaleY(1); opacity: 1; }
}
`;

let gaugeStylesInjected = false;
function injectGaugeStyles() {
  if (gaugeStylesInjected) return;
  gaugeStylesInjected = true;
  const style = document.createElement("style");
  style.textContent = GAUGE_KEYFRAMES;
  document.head.appendChild(style);
}

/* ── Free field types ───────────────────────────────────────────────────── */

interface FreeField {
  id: string;
  label: string;
  values: Record<string, string>;
}

interface ChartSpec {
  type: string;
  title: string;
  rationale: string;
  data: any[];
  config?: any;
}

/* ── Accordion state types ─────────────────────────────────────────────── */

interface AccordionState {
  [formulaCode: string]: { [section: string]: boolean };
}

interface AccordionContentEntry {
  loading: boolean;
  content: string;
  error: string | null;
}

interface AccordionContent {
  [key: string]: AccordionContentEntry;
}

/* ── FormulaAccordion component ────────────────────────────────────────── */

function FormulaAccordion({
  title,
  isOpen,
  onToggle,
  content,
  loading,
  error,
  onGenerate,
  renderContent,
  t,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  content: string;
  loading: boolean;
  error: string | null;
  onGenerate: () => void;
  renderContent?: () => React.ReactNode;
  t: (key: string) => string;
}) {
  return (
    <div style={{
      borderBottom: "1px solid rgba(139,92,246,0.1)",
    }}>
      <button
        onClick={() => { onToggle(); if (!content && !loading) onGenerate(); }}
        style={{
          width: "100%",
          padding: "10px 16px",
          background: "transparent",
          border: "none",
          color: "#c4b5fd",
          fontSize: "13px",
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          textAlign: "left",
        }}
      >
        <span>{title}</span>
        <span style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
          ▸
        </span>
      </button>
      {isOpen && (
        <div style={{ padding: "0 16px 16px" }}>
          {loading && <span style={{ color: "#a78bfa", fontSize: "12px" }}>{t('compare.generating')}</span>}
          {error && <span style={{ color: "#f87171", fontSize: "12px" }}>{error}</span>}
          {renderContent ? renderContent() : null}
        </div>
      )}
    </div>
  );
}

/* ── Paged select past the PostgREST 1000-row cap ───────────────────────── */

// PostgREST silently caps any un-ranged select at 1000 rows — no error,
// just a truncated array. Page with .range() until a short page, ordered
// by a stable column so pages never skip or duplicate rows.
async function selectAllActive<T>(
  table: string,
  columns: string,
  orderCol: string
): Promise<T[]> {
  const PAGE = 1000;
  const all: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from(table)
      .select(columns)
      .eq("is_active", true)
      .order(orderCol, { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const page = (data as T[]) || [];
    all.push(...page);
    if (page.length < PAGE) break;
  }
  return all;
}

/* ── Component ─────────────────────────────────────────────────────────── */

export default function FormulaCompare() {
  const { t, i18n } = useTranslation();
  const ingName = (name: string) => {
    if (i18n.language === 'ko') {
      const ko = nameToKorean[name];
      if (ko) return `${name} (${ko})`;
    }
    return name;
  };
  const claimDisplay: Record<string, string> = {
    "Excellent Source": t('claims.excellentSource'),
    "Good Source": t('claims.goodSource'),
    "No Claim": t('claims.noClaim'),
  };
  const [formulaList, setFormulaList] = useState<FormulaRow[]>([]);
  const [ingredientMap, setIngredientMap] = useState<
    Record<string, CalcIngredient>
  >({});
  const [idToName, setIdToName] = useState<Record<string, string>>({});
  const [nameToKorean, setNameToKorean] = useState<Record<string, string>>({});
  const [slots, setSlots] = useState<string[]>(
    Array(DEFAULT_SLOTS).fill("")
  );
  const [loaded, setLoaded] = useState<Record<string, LoadedFormula>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState<Record<string, boolean>>(
    {}
  );
  const [slotErrors, setSlotErrors] = useState<Record<string, string>>({});
  // Editable serving sizes per formula_code
  const [servingSizes, setServingSizes] = useState<Record<string, number>>({});

  // Free fields state
  const [freeFields, setFreeFields] = useState<FreeField[]>([]);

  // Accordion state
  const [accordionState, setAccordionState] = useState<AccordionState>({});
  const [accordionContent, setAccordionContent] = useState<AccordionContent>({});

  // Free-field charts state
  const [freeFieldChartSpecs, setFreeFieldChartSpecs] = useState<ChartSpec[]>([]);
  const [freeFieldChartsLoading, setFreeFieldChartsLoading] = useState(false);

  /* ── Inject gauge keyframe styles once ────────────────────────────── */

  useEffect(() => { injectGaugeStyles(); }, []);

  /* ── Load formula list + ingredients on mount ─────────────────────── */

  useEffect(() => {
    let cancelled = false;
    async function fetchInitial() {
      try {
        const [fData, iData] = await Promise.all([
          selectAllActive<FormulaRow>(
            "formulas",
            "formula_code, display_name, piece_weight_g, serving_size_g, dough_ratio_pct, single_component, ratio_mode, final_moisture_mode, final_moisture_whole, final_moisture_dough, final_moisture_filling",
            "formula_code"
          ),
          selectAllActive<IngredientRow>("ingredients", "*", "common_name"),
        ]);

        if (cancelled) return;

        setFormulaList(fData);

        const ingMap: Record<string, CalcIngredient> = {};
        const idMap: Record<string, string> = {};
        const koMap: Record<string, string> = {};
        for (const row of iData) {
          const calc = toCalcIngredient(row);
          ingMap[row.common_name] = calc;
          idMap[row.id] = row.common_name;
          if (row.korean_name) koMap[row.common_name] = row.korean_name;
          if (row.id !== row.common_name) {
            ingMap[row.id] = calc;
          }
        }
        setIngredientMap(ingMap);
        setIdToName(idMap);
        setNameToKorean(koMap);
      } catch (err) {
        console.error("FormulaCompare: failed to load initial data", err);
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    }
    fetchInitial();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Load a single formula's components ───────────────────────────── */

  const loadFormula = useCallback(
    async (formulaCode: string) => {
      if (!formulaCode || loaded[formulaCode]) return;

      setLoadingSlots((s) => ({ ...s, [formulaCode]: true }));
      setSlotErrors((s) => ({ ...s, [formulaCode]: "" }));
      try {
        const { data, error } = await supabase
          .from("formula_components")
          .select("*")
          .eq("formula_code", formulaCode);

        if (error) throw error;

        const rows = (data as ComponentRow[]).filter(
          (r) => !r.row_type || r.row_type === "ingredient"
        );

        const dough: Record<string, number> = {};
        const filling: Record<string, number> = {};
        for (const row of rows) {
          const name = idToName[row.ingredient_id] || row.ingredient_id;
          const pct = row.pct_of_component * 100;
          if (row.component === "DOUGH") dough[name] = pct;
          if (row.component === "FILLING") filling[name] = pct;
        }

        const formula = formulaList.find(
          (f) => f.formula_code === formulaCode
        );
        if (!formula) throw new Error(`Unknown formula ${formulaCode}`);

        setLoaded((prev) => ({
          ...prev,
          [formulaCode]: {
            formula,
            doughFormula: dough,
            fillingFormula: filling,
          },
        }));

        // Initialize serving size from formula default
        setServingSizes((prev) => {
          if (prev[formulaCode] != null) return prev;
          return { ...prev, [formulaCode]: formula.serving_size_g || 100 };
        });
      } catch (err: any) {
        console.error(`Failed to load formula ${formulaCode}`, err);
        setSlotErrors((s) => ({
          ...s,
          [formulaCode]: err?.message || `Failed to load ${formulaCode}`,
        }));
      } finally {
        setLoadingSlots((s) => ({ ...s, [formulaCode]: false }));
      }
    },
    [formulaList, idToName, loaded]
  );

  /* ── Slot handlers ────────────────────────────────────────────────── */

  const handleSlotChange = (index: number, formulaCode: string) => {
    setSlots((prev) => {
      const next = [...prev];
      next[index] = formulaCode;
      return next;
    });
    if (formulaCode) loadFormula(formulaCode);
  };

  const handleRemoveSlot = (index: number) => {
    setSlots((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const handleAddSlot = () => {
    if (slots.length < MAX_SLOTS) {
      setSlots((prev) => [...prev, ""]);
    }
  };

  /* ── Free field handlers ─────────────────────────────────────────── */

  const handleAddFreeField = () => {
    setFreeFields(prev => [...prev, {
      id: Date.now().toString(),
      label: "",
      values: {},
    }]);
  };

  const handleRemoveFreeField = (id: string) => {
    setFreeFields(prev => prev.filter(f => f.id !== id));
  };

  const handleFreeFieldLabelChange = (id: string, label: string) => {
    setFreeFields(prev => prev.map(f => f.id === id ? {...f, label} : f));
  };

  const handleFreeFieldValueChange = (id: string, formulaCode: string, value: string) => {
    setFreeFields(prev => prev.map(f =>
      f.id === id ? {...f, values: {...f.values, [formulaCode]: value}} : f
    ));
  };

  /* ── Compute all formula columns ──────────────────────────────────── */

  const activeSlotCodes = slots.filter((code) => code && loaded[code]);

  const computed: ComputedFormula[] = useMemo(() => {
    return activeSlotCodes.map((code) => {
      const lf = loaded[code];
      const f = lf.formula;

      const result = computeFinishedGood(
        lf.doughFormula,
        lf.fillingFormula,
        f.dough_ratio_pct,
        f.ratio_mode || "as_mixed",
        f.final_moisture_mode,
        f.final_moisture_whole,
        f.final_moisture_dough,
        f.final_moisture_filling,
        ingredientMap
      );

      // Build ingredient list sorted by % in finished product (descending)
      const totalFormula = buildTotalFormula(
        lf.doughFormula,
        lf.fillingFormula,
        result.effectiveDoughRatio
      );
      const ingList: { name: string; pct: number }[] = [];
      Object.entries(totalFormula).forEach(([name, pct]) => {
        if (pct > 0) ingList.push({ name, pct });
      });
      ingList.sort((a, b) => b.pct - a.pct);

      return {
        code: f.formula_code,
        displayName: f.display_name,
        formula: f,
        nutrients: result.nutrients,
        pdcaas: result.pdcaas,
        effectiveDoughRatio: result.effectiveDoughRatio,
        effectiveFillingRatio: result.effectiveFillingRatio,
        isPostProcessed: result.isPostProcessed,
        ingredientList: ingList,
      };
    });
  }, [activeSlotCodes.join(","), loaded, ingredientMap]); // eslint-disable-line

  const derivedMetrics: DerivedMetrics[] = useMemo(
    () => computed.map((c) => buildDerivedMetrics(c, ingredientMap)),
    [computed, ingredientMap]
  );

  /* ── Chart specs: AI fetch + fallback ──────────────────────────────── */

  const [chartSpecs, setChartSpecs] = useState<ChartSpec[]>([]);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [chartsError, setChartsError] = useState<string | null>(null);
  const lastChartKey = useRef<string>("");

  // Build fallback charts from derived metrics
  function buildFallbackCharts(metrics: DerivedMetrics[]): ChartSpec[] {
    const PALETTE = ["#8b5cf6", "#f59e0b", "#22c55e", "#ef4444", "#06b6d4", "#f472b6", "#84cc16", "#e879f9", "#fb923c", "#2dd4bf"];

    // 1. Radar: Satiety Nutrient Density per NET calorie
    const radarMetrics = [
      { key: "protein_per_net_kcal", label: t('compare.totalProtein') },
      { key: "corrected_protein_per_net_kcal", label: t('compare.correctedProtein') },
      { key: "soluble_fiber_per_net_kcal", label: t('compare.solubleFiber') },
      { key: "oleic_per_net_kcal", label: t('compare.oleicAcid') },
      { key: "iron_per_net_kcal", label: t('compare.iron') },
      { key: "ala_per_net_kcal", label: t('compare.alaOmega3') },
    ];
    // Normalize each metric to 0-100 across formulas
    const radarData = radarMetrics.map((rm) => {
      const vals = metrics.map((m) => (m as any)[rm.key] as number);
      const maxVal = Math.max(...vals, 0.001);
      const row: any = { metric: rm.label, fullMark: 100 };
      metrics.forEach((m) => {
        row[m.formula_code] = ((m as any)[rm.key] / maxVal) * 100;
      });
      return row;
    });

    // 2. Grouped bar: Total vs Corrected Protein per net kcal (shows quality gap)
    const proteinQualityData = metrics.map((m) => ({
      name: m.formula_code,
      [t('compare.totalProtein')]: +(m.protein_per_net_kcal).toFixed(2),
      [t('compare.correctedProtein')]: +(m.corrected_protein_per_net_kcal).toFixed(2),
      [t('compare.qualityGap')]: +((m.protein_per_net_kcal - m.corrected_protein_per_net_kcal)).toFixed(2),
    }));

    // 3. Stacked bar: Ingredient composition by beneficial category
    const ingredientStackData = metrics.map((m) => ({
      name: m.formula_code,
      [t('compare.proteinSources')]: m.pct_protein_sources,
      [t('compare.fiberSources')]: m.pct_fiber_sources,
      [t('compare.healthyFats')]: m.pct_healthy_fat_sources,
      [t('compare.otherBeneficial')]: Math.max(
        0,
        m.pct_beneficial_ingredients -
          m.pct_protein_sources -
          m.pct_fiber_sources -
          m.pct_healthy_fat_sources
      ),
      [t('compare.nonBeneficial')]: 100 - m.pct_beneficial_ingredients,
    }));

    return [
      {
        type: "radar",
        title: t('compare.satietyDensity'),
        rationale: "Each axis = (nutrient_per_100g / net_kcal) * 100, where net_kcal = calories - fiber*4 (fiber provides ~0 usable energy). Normalized 0-100 across formulas so different units are comparable. Shows which formulas pack more satiety-driving nutrients per net calorie.",
        data: radarData,
        config: { formulaKeys: metrics.map((m) => m.formula_code), colors: PALETTE },
      },
      {
        type: "groupedBar",
        title: t('compare.proteinQualityGap'),
        rationale: "Total protein per net 100kcal vs PDCAAS-corrected protein per net 100kcal. The gap between bars shows how much protein is lost to digestibility and amino acid limitations. Smaller gap = higher quality protein blend.",
        data: proteinQualityData,
        config: {
          metrics: [
            { key: t('compare.totalProtein'), label: t('compare.totalProteinG100kcal'), color: "#8b5cf6" },
            { key: t('compare.correctedProtein'), label: t('compare.correctedProteinG100kcal'), color: "#22c55e" },
          ],
          colors: PALETTE,
        },
      },
      {
        type: "stackedBar",
        title: t('compare.whatAreFormulas'),
        rationale: "Breaks down each formula's ingredient weight into categories: protein sources (>15% protein), fiber sources (>5% fiber or high soluble/resistant starch), healthy fats (high oleic or ALA), other beneficial (micronutrient-dense), and non-beneficial (fillers, binders, sugar, sodium-heavy). A formula with more colored = more of its weight pulling nutritional duty.",
        data: ingredientStackData,
        config: {
          segments: [
            { key: t('compare.proteinSources'), label: t('compare.proteinSourcesPct'), color: "#8b5cf6" },
            { key: t('compare.fiberSources'), label: t('compare.fiberSourcesPct'), color: "#22c55e" },
            { key: t('compare.healthyFats'), label: t('compare.healthyFatsPct'), color: "#06b6d4" },
            { key: t('compare.otherBeneficial'), label: t('compare.otherBeneficialPct'), color: "#f59e0b" },
            { key: t('compare.nonBeneficial'), label: t('compare.nonBeneficialPct'), color: "#334155" },
          ],
        },
      },
    ];
  }

  // Fetch AI charts when derivedMetrics change
  useEffect(() => {
    if (derivedMetrics.length < 2) {
      setChartSpecs([]);
      lastChartKey.current = "";
      return;
    }

    const key = `${i18n.language}|${derivedMetrics.map((m) => m.formula_code).sort().join("|")}`;
    if (key === lastChartKey.current) return;
    lastChartKey.current = key;

    let cancelled = false;
    setChartsLoading(true);
    setChartsError(null);

    fetch("/api/compare-charts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ formulas: derivedMetrics, lang: i18n.language }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled && json.charts) {
          setChartSpecs(json.charts);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn(t('compare.aiChartsFailed'), err.message);
        setChartsError(t('compare.aiUnavailableFallback'));
        setChartSpecs(buildFallbackCharts(derivedMetrics));
      })
      .finally(() => {
        if (!cancelled) setChartsLoading(false);
      });

    return () => { cancelled = true; };
  }, [derivedMetrics, i18n.language]); // eslint-disable-line

  /* ── AI Insights Report ──────────────────────────────────────────── */

  const [insightsReport, setInsightsReport] = useState<string>("");
  const [insightsSources, setInsightsSources] = useState<{ title: string; url: string }[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  // Clear a stale report when the compared formula set changes
  const computedCodeSet = computed.map((c) => c.code).join("|");
  useEffect(() => {
    setInsightsReport("");
    setInsightsSources([]);
    setInsightsError(null);
  }, [computedCodeSet]);

  const generateInsights = useCallback(async () => {
    if (derivedMetrics.length < 1) return;
    setInsightsLoading(true);
    setInsightsError(null);
    setInsightsReport("");
    setInsightsSources([]);

    // Build ingredient metadata for the AI
    const ingredientMetadata: Record<string, any> = {};
    for (const c of computed) {
      for (const ing of c.ingredientList) {
        if (ingredientMetadata[ing.name]) continue;
        const data = ingredientMap[ing.name];
        if (data) {
          ingredientMetadata[ing.name] = {
            protein_per_100g: data.protein_per_100g,
            digestibility: data.digestibility,
            fat: data.fat,
            satfat: data.satfat,
            carbs: data.carbs,
            fiber: data.fiber,
            sugar: data.sugar,
            sodium: data.sodium,
            moisture: data.moisture,
            iron: data.iron,
            calcium: data.calcium,
            potassium: data.potassium,
            fa_ala: data.fa_ala ?? 0,
            fa_oleic: data.fa_oleic ?? 0,
            fa_la: data.fa_la ?? 0,
            fiber_soluble: data.fiber_soluble ?? 0,
            fiber_insoluble: data.fiber_insoluble ?? 0,
            resistant_starch: data.resistant_starch ?? 0,
          };
        }
      }
    }

    // Build formula metadata with ALL pre-computed values
    const formulaMetadata = computed.map((c) => {
      const ss = servingSizes[c.code] ?? c.formula.serving_size_g;
      const n = c.nutrients;
      const ps = (v: number) => scaleToServing(v, ss);
      const fdaV = (nutrientKey: string, per100g: number) => {
        const perServing = ps(per100g);
        const rounder = (fdaRound as any)[nutrientKey];
        return rounder ? rounder(perServing) : perServing;
      };
      const correctedPerServing = ps(c.pdcaas.correctedProteinPer100g);
      const proteinPctDV = (correctedPerServing / DRV.protein) * 100;
      const fiberPctDV = (ps(n.fiber) / DRV.fiber) * 100;

      return {
        formula_code: c.code,
        display_name: c.displayName,
        serving_size_g: ss,
        piece_weight_g: c.formula.piece_weight_g,
        pieces_per_serving: c.formula.piece_weight_g > 0 ? ss / c.formula.piece_weight_g : null,
        dough_ratio: c.effectiveDoughRatio,
        filling_ratio: c.effectiveFillingRatio,
        is_post_processed: c.isPostProcessed,
        ratio_mode: c.formula.ratio_mode,
        ingredients_descending: c.ingredientList.map(
          (i) => `${i.name} (${i.pct.toFixed(1)}%)`
        ),
        // Pre-computed per 100g
        per_100g: {
          calories: n.calories,
          protein: n.protein,
          fat: n.fat,
          satfat: n.satfat,
          transfat: n.transfat,
          cholesterol: n.cholesterol,
          carbs: n.carbs,
          fiber: n.fiber,
          sugar: n.sugar,
          sodium: n.sodium,
          moisture: n.moisture,
          vitaminD: n.vitaminD,
          calcium: n.calcium,
          iron: n.iron,
          potassium: n.potassium,
          fiber_soluble: n.fiberSoluble,
          fiber_insoluble: n.fiberInsoluble,
          resistant_starch: n.resistantStarch,
          fa_ala: n.faAla,
          fa_la: n.faLa,
          fa_oleic: n.faOleic,
          fa_palmitic: n.faPalmitic,
          fa_stearic: n.faStearic,
          n6_n3_ratio: n.faAla > 0 ? n.faLa / n.faAla : null,
        },
        // Pre-computed per serving (full precision)
        per_serving: {
          calories: ps(n.calories),
          protein: ps(n.protein),
          fat: ps(n.fat),
          satfat: ps(n.satfat),
          transfat: ps(n.transfat),
          cholesterol: ps(n.cholesterol),
          carbs: ps(n.carbs),
          fiber: ps(n.fiber),
          sugar: ps(n.sugar),
          sodium: ps(n.sodium),
          vitaminD: ps(n.vitaminD),
          calcium: ps(n.calcium),
          iron: ps(n.iron),
          potassium: ps(n.potassium),
        },
        // Pre-computed FDA-rounded per serving
        fda_rounded: {
          calories: fdaV("calories", n.calories),
          fat: fdaV("fat", n.fat),
          satfat: fdaV("satfat", n.satfat),
          transfat: fdaV("transfat", n.transfat),
          cholesterol: fdaV("cholesterol", n.cholesterol),
          sodium: fdaV("sodium", n.sodium),
          carbs: fdaV("carbs", n.carbs),
          fiber: fdaV("fiber", n.fiber),
          sugar: fdaV("sugar", n.sugar),
          protein: fdaV("protein", n.protein),
          vitaminD: fdaV("vitaminD", n.vitaminD),
          calcium: fdaV("calcium", n.calcium),
          iron: fdaV("iron", n.iron),
          potassium: fdaV("potassium", n.potassium),
        },
        // Pre-computed protein quality
        protein_quality: {
          weighted_digestibility: c.pdcaas.weightedDigestibility,
          amino_acid_score: c.pdcaas.aminoAcidScore,
          pdcaas: c.pdcaas.truncatedPdcaas,
          limiting_aa: c.pdcaas.limitingAA
            ? AA_FULL_NAMES[c.pdcaas.limitingAA] || c.pdcaas.limitingAA
            : null,
          corrected_protein_per_100g: c.pdcaas.correctedProteinPer100g,
          corrected_protein_per_serving: correctedPerServing,
          blended_amino_acids_mg_per_g_protein: c.pdcaas.blendedAA,
          leucine_mg_per_g_protein: c.pdcaas.blendedAA?.leucine ?? 0,
          leucine_g_per_serving: (ps(n.protein) * (c.pdcaas.blendedAA?.leucine ?? 0)) / 1000,
          bcaa_mg_per_g_protein:
            (c.pdcaas.blendedAA?.leucine ?? 0) +
            (c.pdcaas.blendedAA?.isoleucine ?? 0) +
            (c.pdcaas.blendedAA?.valine ?? 0),
        },
        // Pre-computed claims
        claims: {
          protein_pct_dv: proteinPctDV,
          protein_claim: getProteinClaim(proteinPctDV).claim,
          fiber_pct_dv: fiberPctDV,
          fiber_claim: fiberPctDV >= 20 ? "Excellent Source" : fiberPctDV >= 10 ? "Good Source" : "No Claim",
          protein_quality_gap_pct: n.protein > 0
            ? ((n.protein - c.pdcaas.correctedProteinPer100g) / n.protein) * 100
            : 0,
        },
      };
    });

    try {
      const response = await fetch("/api/compare-insights", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formulas: derivedMetrics,
          chartSpecs,
          ingredientMetadata,
          formulaMetadata,
          freeFields: freeFields
            .filter(ff => ff.label.trim() && Object.values(ff.values).some(v => v.trim()))
            .map(ff => ({ label: ff.label, values: ff.values })),
          lang: i18n.language,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setInsightsReport(data.report || t('compare.noReportGenerated'));
      setInsightsSources(data.sources || []);
    } catch (err: any) {
      console.error("Insights generation failed:", err);
      setInsightsError(err.message || t('compare.insightsFailed'));
    } finally {
      setInsightsLoading(false);
    }
  }, [derivedMetrics, computed, ingredientMap, servingSizes, chartSpecs, freeFields]);

  /* ── Accordion section generation ────────────────────────────────── */

  const toggleAccordion = (formulaCode: string, section: string) => {
    setAccordionState(prev => ({
      ...prev,
      [formulaCode]: {
        ...prev[formulaCode],
        [section]: !prev[formulaCode]?.[section],
      },
    }));
  };

  const generateBatchAccordionContent = useCallback(async (formulaCode: string) => {
    // Check if already loaded or loading (include language in cache key so switching language invalidates)
    const lang = i18n.language;
    const sections = ["clinical", "claims", "nova", "vs_foods"];
    const cacheKey = (s: string) => `${formulaCode}-${s}-${lang}`;
    const allLoaded = sections.every(s => accordionContent[cacheKey(s)]?.content);
    const anyLoading = sections.some(s => accordionContent[cacheKey(s)]?.loading);
    if (allLoaded || anyLoading) return;

    // Mark all 4 as loading
    setAccordionContent(prev => {
      const next = { ...prev };
      for (const s of sections) {
        next[cacheKey(s)] = { loading: true, content: "", error: null };
      }
      return next;
    });

    const c = computed.find(cc => cc.code === formulaCode);
    if (!c) return;

    const ss = servingSizes[formulaCode] ?? c.formula.serving_size_g;
    const n = c.nutrients;
    const ps = (v: number) => scaleToServing(v, ss);
    const fdaV = (nutrientKey: string, per100g: number) => {
      const perServing = ps(per100g);
      const rounder = (fdaRound as any)[nutrientKey];
      return rounder ? rounder(perServing) : perServing;
    };
    const correctedPerServing = ps(c.pdcaas.correctedProteinPer100g);
    const proteinPctDV = (correctedPerServing / DRV.protein) * 100;
    const fiberPctDV = (ps(n.fiber) / DRV.fiber) * 100;

    const formulaData = {
      formula_code: c.code,
      display_name: c.displayName,
      serving_size_g: ss,
      piece_weight_g: c.formula.piece_weight_g,
      ingredients_descending: c.ingredientList.map(i => `${i.name} (${i.pct.toFixed(1)}%)`),
      per_100g: {
        calories: n.calories, protein: n.protein, fat: n.fat, satfat: n.satfat,
        transfat: n.transfat, cholesterol: n.cholesterol, carbs: n.carbs,
        fiber: n.fiber, sugar: n.sugar, sodium: n.sodium, moisture: n.moisture,
        vitaminD: n.vitaminD, calcium: n.calcium, iron: n.iron, potassium: n.potassium,
        fiber_soluble: n.fiberSoluble, fiber_insoluble: n.fiberInsoluble,
        resistant_starch: n.resistantStarch, fa_ala: n.faAla, fa_la: n.faLa,
        fa_oleic: n.faOleic, fa_palmitic: n.faPalmitic, fa_stearic: n.faStearic,
        n6_n3_ratio: n.faAla > 0 ? n.faLa / n.faAla : null,
      },
      per_serving: {
        calories: ps(n.calories), protein: ps(n.protein), fat: ps(n.fat),
        satfat: ps(n.satfat), transfat: ps(n.transfat), cholesterol: ps(n.cholesterol),
        carbs: ps(n.carbs), fiber: ps(n.fiber), sugar: ps(n.sugar), sodium: ps(n.sodium),
        vitaminD: ps(n.vitaminD), calcium: ps(n.calcium), iron: ps(n.iron), potassium: ps(n.potassium),
      },
      fda_rounded: {
        calories: fdaV("calories", n.calories), fat: fdaV("fat", n.fat),
        satfat: fdaV("satfat", n.satfat), transfat: fdaV("transfat", n.transfat),
        cholesterol: fdaV("cholesterol", n.cholesterol), sodium: fdaV("sodium", n.sodium),
        carbs: fdaV("carbs", n.carbs), fiber: fdaV("fiber", n.fiber),
        sugar: fdaV("sugar", n.sugar), protein: fdaV("protein", n.protein),
        vitaminD: fdaV("vitaminD", n.vitaminD), calcium: fdaV("calcium", n.calcium),
        iron: fdaV("iron", n.iron), potassium: fdaV("potassium", n.potassium),
      },
      protein_quality: {
        weighted_digestibility: c.pdcaas.weightedDigestibility,
        amino_acid_score: c.pdcaas.aminoAcidScore,
        pdcaas: c.pdcaas.truncatedPdcaas,
        limiting_aa: c.pdcaas.limitingAA ? AA_FULL_NAMES[c.pdcaas.limitingAA] || c.pdcaas.limitingAA : null,
        corrected_protein_per_100g: c.pdcaas.correctedProteinPer100g,
        corrected_protein_per_serving: correctedPerServing,
        blended_amino_acids_mg_per_g_protein: c.pdcaas.blendedAA,
        leucine_mg_per_g_protein: c.pdcaas.blendedAA?.leucine ?? 0,
        leucine_g_per_serving: (ps(n.protein) * (c.pdcaas.blendedAA?.leucine ?? 0)) / 1000,
        bcaa_mg_per_g_protein: (c.pdcaas.blendedAA?.leucine ?? 0) + (c.pdcaas.blendedAA?.isoleucine ?? 0) + (c.pdcaas.blendedAA?.valine ?? 0),
      },
      claims: {
        protein_pct_dv: proteinPctDV,
        protein_claim: getProteinClaim(proteinPctDV).claim,
        fiber_pct_dv: fiberPctDV,
        fiber_claim: fiberPctDV >= 20 ? "Excellent Source" : fiberPctDV >= 10 ? "Good Source" : "No Claim",
        protein_quality_gap_pct: n.protein > 0 ? ((n.protein - c.pdcaas.correctedProteinPer100g) / n.protein) * 100 : 0,
      },
    };

    const relevantIngredients: Record<string, any> = {};
    for (const ing of c.ingredientList) {
      const data = ingredientMap[ing.name];
      if (data) {
        relevantIngredients[ing.name] = {
          protein_per_100g: data.protein_per_100g, digestibility: data.digestibility,
          fat: data.fat, satfat: data.satfat, fiber: data.fiber, sugar: data.sugar,
          sodium: data.sodium, iron: data.iron, calcium: data.calcium, potassium: data.potassium,
          fa_ala: data.fa_ala ?? 0, fa_oleic: data.fa_oleic ?? 0, fa_la: data.fa_la ?? 0,
          fiber_soluble: data.fiber_soluble ?? 0, resistant_starch: data.resistant_starch ?? 0,
        };
      }
    }

    try {
      const res = await fetch("/api/compare-section", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          batch: true,
          formulaData,
          ingredientMetadata: relevantIngredients,
          lang: i18n.language,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.sections) {
        setAccordionContent(prev => {
          const next = { ...prev };
          for (const s of sections) {
            next[cacheKey(s)] = {
              loading: false,
              content: data.sections[s] || "",
              error: data.sections[s] ? null : t('compare.noContentReturned'),
            };
          }
          return next;
        });
      }
    } catch (err: any) {
      setAccordionContent(prev => {
        const next = { ...prev };
        for (const s of sections) {
          next[cacheKey(s)] = { loading: false, content: "", error: err.message };
        }
        return next;
      });
    }
  }, [computed, servingSizes, ingredientMap, accordionContent]);

  const generateAccordionContent = useCallback(async (formulaCode: string, section: string) => {
    const key = `${formulaCode}-${section}-${i18n.language}`;
    if (accordionContent[key]?.content || accordionContent[key]?.loading) return;

    setAccordionContent(prev => ({ ...prev, [key]: { loading: true, content: "", error: null } }));

    const c = computed.find(cc => cc.code === formulaCode);
    if (!c) return;

    const ss = servingSizes[formulaCode] ?? c.formula.serving_size_g;
    const n = c.nutrients;
    const ps = (v: number) => scaleToServing(v, ss);
    const fdaV = (nutrientKey: string, per100g: number) => {
      const perServing = ps(per100g);
      const rounder = (fdaRound as any)[nutrientKey];
      return rounder ? rounder(perServing) : perServing;
    };
    const correctedPerServing = ps(c.pdcaas.correctedProteinPer100g);
    const proteinPctDV = (correctedPerServing / DRV.protein) * 100;
    const fiberPctDV = (ps(n.fiber) / DRV.fiber) * 100;

    const formulaData = {
      formula_code: c.code,
      display_name: c.displayName,
      serving_size_g: ss,
      piece_weight_g: c.formula.piece_weight_g,
      ingredients_descending: c.ingredientList.map(i => `${i.name} (${i.pct.toFixed(1)}%)`),
      per_100g: {
        calories: n.calories, protein: n.protein, fat: n.fat, satfat: n.satfat,
        transfat: n.transfat, cholesterol: n.cholesterol, carbs: n.carbs,
        fiber: n.fiber, sugar: n.sugar, sodium: n.sodium, moisture: n.moisture,
        vitaminD: n.vitaminD, calcium: n.calcium, iron: n.iron, potassium: n.potassium,
        fiber_soluble: n.fiberSoluble, fiber_insoluble: n.fiberInsoluble,
        resistant_starch: n.resistantStarch, fa_ala: n.faAla, fa_la: n.faLa,
        fa_oleic: n.faOleic, fa_palmitic: n.faPalmitic, fa_stearic: n.faStearic,
        n6_n3_ratio: n.faAla > 0 ? n.faLa / n.faAla : null,
      },
      per_serving: {
        calories: ps(n.calories), protein: ps(n.protein), fat: ps(n.fat),
        satfat: ps(n.satfat), transfat: ps(n.transfat), cholesterol: ps(n.cholesterol),
        carbs: ps(n.carbs), fiber: ps(n.fiber), sugar: ps(n.sugar), sodium: ps(n.sodium),
        vitaminD: ps(n.vitaminD), calcium: ps(n.calcium), iron: ps(n.iron), potassium: ps(n.potassium),
      },
      fda_rounded: {
        calories: fdaV("calories", n.calories), fat: fdaV("fat", n.fat),
        satfat: fdaV("satfat", n.satfat), transfat: fdaV("transfat", n.transfat),
        cholesterol: fdaV("cholesterol", n.cholesterol), sodium: fdaV("sodium", n.sodium),
        carbs: fdaV("carbs", n.carbs), fiber: fdaV("fiber", n.fiber),
        sugar: fdaV("sugar", n.sugar), protein: fdaV("protein", n.protein),
        vitaminD: fdaV("vitaminD", n.vitaminD), calcium: fdaV("calcium", n.calcium),
        iron: fdaV("iron", n.iron), potassium: fdaV("potassium", n.potassium),
      },
      protein_quality: {
        weighted_digestibility: c.pdcaas.weightedDigestibility,
        amino_acid_score: c.pdcaas.aminoAcidScore,
        pdcaas: c.pdcaas.truncatedPdcaas,
        limiting_aa: c.pdcaas.limitingAA ? AA_FULL_NAMES[c.pdcaas.limitingAA] || c.pdcaas.limitingAA : null,
        corrected_protein_per_100g: c.pdcaas.correctedProteinPer100g,
        corrected_protein_per_serving: correctedPerServing,
        blended_amino_acids_mg_per_g_protein: c.pdcaas.blendedAA,
        leucine_mg_per_g_protein: c.pdcaas.blendedAA?.leucine ?? 0,
        leucine_g_per_serving: (ps(n.protein) * (c.pdcaas.blendedAA?.leucine ?? 0)) / 1000,
        bcaa_mg_per_g_protein: (c.pdcaas.blendedAA?.leucine ?? 0) + (c.pdcaas.blendedAA?.isoleucine ?? 0) + (c.pdcaas.blendedAA?.valine ?? 0),
      },
      claims: {
        protein_pct_dv: proteinPctDV,
        protein_claim: getProteinClaim(proteinPctDV).claim,
        fiber_pct_dv: fiberPctDV,
        fiber_claim: fiberPctDV >= 20 ? "Excellent Source" : fiberPctDV >= 10 ? "Good Source" : "No Claim",
        protein_quality_gap_pct: n.protein > 0 ? ((n.protein - c.pdcaas.correctedProteinPer100g) / n.protein) * 100 : 0,
      },
    };

    const relevantIngredients: Record<string, any> = {};
    for (const ing of c.ingredientList) {
      const data = ingredientMap[ing.name];
      if (data) {
        relevantIngredients[ing.name] = {
          protein_per_100g: data.protein_per_100g, digestibility: data.digestibility,
          fat: data.fat, satfat: data.satfat, fiber: data.fiber, sugar: data.sugar,
          sodium: data.sodium, iron: data.iron, calcium: data.calcium, potassium: data.potassium,
          fa_ala: data.fa_ala ?? 0, fa_oleic: data.fa_oleic ?? 0, fa_la: data.fa_la ?? 0,
          fiber_soluble: data.fiber_soluble ?? 0, resistant_starch: data.resistant_starch ?? 0,
        };
      }
    }

    try {
      const res = await fetch("/api/compare-section", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          section,
          formulaData,
          ingredientMetadata: relevantIngredients,
          lang: i18n.language,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAccordionContent(prev => ({
        ...prev,
        [key]: { loading: false, content: data.content, error: null },
      }));
    } catch (err: any) {
      setAccordionContent(prev => ({
        ...prev,
        [key]: { loading: false, content: "", error: err.message },
      }));
    }
  }, [computed, servingSizes, ingredientMap]);

  /* ── Free-field chart generation ─────────────────────────────────── */

  const generateFreeFieldCharts = useCallback(async () => {
    const filledFields = freeFields
      .filter(ff => ff.label.trim() && Object.values(ff.values).some(v => v.trim()))
      .map(ff => ({
        label: ff.label,
        values: Object.fromEntries(
          Object.entries(ff.values).filter(([_, v]) => v.trim())
        ),
      }));

    if (filledFields.length === 0) return;

    setFreeFieldChartsLoading(true);
    try {
      const res = await fetch("/api/compare-freefield-charts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formulas: derivedMetrics, freeFields: filledFields, lang: i18n.language }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.charts) setFreeFieldChartSpecs(data.charts);
    } catch (err) {
      console.warn(t('compare.freeFieldChartsFailed'), err);
    } finally {
      setFreeFieldChartsLoading(false);
    }
  }, [freeFields, derivedMetrics]);

  /* ── Simple markdown renderer ────────────────────────────────────── */

  function renderMarkdown(md: string): React.ReactNode {
    const lines = md.split("\n");
    const elements: React.ReactNode[] = [];
    let inList = false;
    let listItems: React.ReactNode[] = [];

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul
            key={`list-${elements.length}`}
            style={{ margin: "6px 0 12px 16px", padding: 0, lineHeight: 1.7 }}
          >
            {listItems}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    const renderInline = (text: string): React.ReactNode => {
      // Bold
      const parts: React.ReactNode[] = [];
      const boldRegex = /\*\*(.+?)\*\*/g;
      let lastIndex = 0;
      let match;
      while ((match = boldRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.slice(lastIndex, match.index));
        }
        parts.push(
          <strong key={match.index} style={{ color: "#e2e8f0" }}>
            {match[1]}
          </strong>
        );
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) parts.push(text.slice(lastIndex));
      return parts.length === 1 ? parts[0] : <>{parts}</>;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      // Headers
      if (trimmed.startsWith("### ")) {
        flushList();
        elements.push(
          <h4
            key={`h4-${i}`}
            style={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#c4b5fd",
              margin: "24px 0 8px",
              borderBottom: "1px solid rgba(139,92,246,0.2)",
              paddingBottom: "4px",
            }}
          >
            {trimmed.slice(4)}
          </h4>
        );
        continue;
      }
      if (trimmed.startsWith("## ")) {
        flushList();
        elements.push(
          <h3
            key={`h3-${i}`}
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#e0d4fd",
              margin: "28px 0 10px",
              borderBottom: "2px solid rgba(139,92,246,0.3)",
              paddingBottom: "6px",
            }}
          >
            {trimmed.slice(3)}
          </h3>
        );
        continue;
      }

      // Bullet list
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        inList = true;
        listItems.push(
          <li key={`li-${i}`} style={{ color: "#cbd5e1", fontSize: "13px" }}>
            {renderInline(trimmed.slice(2))}
          </li>
        );
        continue;
      }

      // Numbered list
      if (/^\d+\.\s/.test(trimmed)) {
        inList = true;
        listItems.push(
          <li key={`li-${i}`} style={{ color: "#cbd5e1", fontSize: "13px" }}>
            {renderInline(trimmed.replace(/^\d+\.\s/, ""))}
          </li>
        );
        continue;
      }

      // Empty line
      if (trimmed === "") {
        flushList();
        continue;
      }

      // Horizontal rule
      if (trimmed === "---" || trimmed === "***") {
        flushList();
        elements.push(
          <hr
            key={`hr-${i}`}
            style={{
              border: "none",
              borderTop: "1px solid rgba(139,92,246,0.2)",
              margin: "16px 0",
            }}
          />
        );
        continue;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <p
          key={`p-${i}`}
          style={{
            color: "#cbd5e1",
            fontSize: "13px",
            lineHeight: 1.7,
            margin: "8px 0",
          }}
        >
          {renderInline(trimmed)}
        </p>
      );
    }
    flushList();
    return elements;
  }

  /* ── Excel export ────────────────────────────────────────────────── */

  const downloadExcel = useCallback(() => {
    if (computed.length === 0) return;

    const numCols = 1 + computed.length; // label col + formula cols

    type RowDef =
      | { type: "section"; title: string }
      | { type: "data"; label: string; values: (string | number)[] };

    const rows: RowDef[] = [];

    function addSection(title: string) {
      rows.push({ type: "section", title });
    }

    function addRow(label: string, getter: (c: ComputedFormula) => string | number) {
      rows.push({
        type: "data",
        label,
        values: computed.map(getter),
      });
    }

    // Numeric value helper — keeps numbers as numbers for Excel
    const num = (v: number) => v;

    // ─── A: Formula Identity
    addSection(t('compare.sectionA'));
    addRow(t('compare.formulaCode'), (c) => c.code);
    addRow(t('compare.displayName'), (c) => c.displayName);
    addRow(t('compare.components'), (c) =>
      c.formula.single_component ||
      Object.keys(loaded[c.code]?.fillingFormula || {}).length === 0
        ? t('compare.single')
        : t('compare.doughPlusFilling')
    );
    addRow(t('compare.ratioMode'), (c) =>
      c.formula.ratio_mode === "finished" ? t('compare.postProcessLabel') : t('compare.asMixedLabel')
    );
    addRow(t('compare.doughRatioPct'), (c) => num(c.effectiveDoughRatio));
    addRow(t('compare.fillingRatioPct'), (c) => num(c.effectiveFillingRatio));
    addRow(t('compare.postProcess'), (c) => {
      if (!c.isPostProcessed) return t('common.no');
      const mode = c.formula.final_moisture_mode;
      if (mode === "composite")
        return `${t('common.yes')} - ${t('compare.compositeLabel')} ${(c.formula.final_moisture_whole ?? 0).toFixed(1)}%`;
      if (mode === "components")
        return `${t('common.yes')} - ${t('formulation.doughLabel')} ${(c.formula.final_moisture_dough ?? 0).toFixed(1)}% / ${t('formulation.fillingLabel')} ${(c.formula.final_moisture_filling ?? 0).toFixed(1)}%`;
      return t('common.yes');
    });

    // ─── B: Macronutrients per 100g
    addSection(t('compare.sectionB'));
    addRow(t('compare.moisture'), (c) => num(c.nutrients.moisture));
    addRow(t('compare.proteinG'), (c) => num(c.nutrients.protein));
    addRow(t('compare.totalFatG'), (c) => num(c.nutrients.fat));
    addRow(t('compare.saturatedFatG'), (c) => num(c.nutrients.satfat));
    addRow(t('compare.transFatG'), (c) => num(c.nutrients.transfat));
    addRow(t('compare.cholesterolMg'), (c) => num(c.nutrients.cholesterol));
    addRow(t('compare.totalCarbG'), (c) => num(c.nutrients.carbs));
    addRow(t('compare.totalDietaryFiberG'), (c) => num(c.nutrients.fiber));
    addRow(t('compare.totalSugarsG'), (c) => num(c.nutrients.sugar));
    addRow(t('compare.sodiumMg'), (c) => num(c.nutrients.sodium));
    addRow(t('compare.caloriesKcal'), (c) => num(c.nutrients.calories));

    // ─── C: Micronutrients per 100g
    addSection(t('compare.sectionC'));
    addRow(t('compare.vitaminDMcg'), (c) => num(c.nutrients.vitaminD));
    addRow(t('compare.calciumMg'), (c) => num(c.nutrients.calcium));
    addRow(t('compare.ironMg'), (c) => num(c.nutrients.iron));
    addRow(t('compare.potassiumMg'), (c) => num(c.nutrients.potassium));

    // ─── D: Fiber Fractions
    addSection(t('compare.sectionD'));
    addRow(t('compare.insolubleFiberG'), (c) => num(c.nutrients.fiberInsoluble));
    addRow(t('compare.solubleFiberG'), (c) => num(c.nutrients.fiberSoluble));
    addRow(t('compare.resistantStarchG'), (c) => num(c.nutrients.resistantStarch));

    // ─── E: Fatty Acid Profile
    addSection(t('compare.sectionE'));
    addRow(t('compare.ala'), (c) => num(c.nutrients.faAla));
    addRow(t('compare.la'), (c) => num(c.nutrients.faLa));
    addRow(t('compare.oleic'), (c) => num(c.nutrients.faOleic));
    addRow(t('compare.palmitic'), (c) => num(c.nutrients.faPalmitic));
    addRow(t('compare.stearic'), (c) => num(c.nutrients.faStearic));
    addRow(t('compare.n6n3Ratio'), (c) =>
      c.nutrients.faAla > 0 ? num(c.nutrients.faLa / c.nutrients.faAla) : t('compare.na')
    );

    // ─── F: Amino Acid Profile
    addSection(t('compare.sectionF'));
    Object.keys(REFERENCE_PATTERN).forEach((aa) => {
      addRow(`${AA_NAMES[aa]} (${AA_FULL_NAMES[aa]})`, (c) =>
        num(c.pdcaas.blendedAA[aa] ?? 0)
      );
    });
    addRow(t('compare.bcaa'), (c) =>
      num(
        (c.pdcaas.blendedAA.leucine ?? 0) +
        (c.pdcaas.blendedAA.isoleucine ?? 0) +
        (c.pdcaas.blendedAA.valine ?? 0)
      )
    );

    // ─── G: PDCAAS
    addSection(t('compare.sectionG'));
    addRow(t('compare.weightedDigestibility'), (c) => num(c.pdcaas.weightedDigestibility));
    addRow(t('compare.limitingAminoAcid'), (c) =>
      c.pdcaas.limitingAA
        ? AA_FULL_NAMES[c.pdcaas.limitingAA] || c.pdcaas.limitingAA
        : "—"
    );
    addRow(t('compare.aminoAcidScorePct'), (c) => num(c.pdcaas.aminoAcidScore * 100));
    addRow(t('compare.pdcaasLabel'), (c) => num(c.pdcaas.truncatedPdcaas));
    addRow(t('compare.correctedProteinG100g'), (c) => num(c.pdcaas.correctedProteinPer100g));

    // ─── H: Ingredient List
    addSection(t('compare.sectionH'));
    addRow(t('compare.ingredients'), (c) =>
      c.ingredientList
        .map((ing) => `${ingName(ing.name)} (${ing.pct.toFixed(1)}%)`)
        .join("\n")
    );

    // ─── I: Per Serving Parameters
    addSection(t('compare.sectionI'));
    addRow(t('compare.servingSizeG'), (c) =>
      num(servingSizes[c.code] ?? c.formula.serving_size_g)
    );
    addRow(t('compare.piecesPerServing'), (c) => {
      const ss = servingSizes[c.code] ?? c.formula.serving_size_g;
      const pw = c.formula.piece_weight_g;
      return pw > 0 ? num(ss / pw) : "—";
    });

    // ─── J: FDA Nutrition Facts (rounded — export as displayed)
    addSection(t('compare.sectionJ'));
    const fdaRowDefs: [string, string][] = [
      [t('compare.calories'), "calories"],
      [t('compare.totalFatG'), "fat"],
      [t('compare.saturatedFatG'), "satfat"],
      [t('compare.transFatG'), "transfat"],
      [t('compare.cholesterolMg'), "cholesterol"],
      [t('compare.sodiumMg'), "sodium"],
      [t('compare.totalCarbG'), "carbs"],
      [t('compare.dietaryFiberG'), "fiber"],
      [t('compare.totalSugarsG'), "sugar"],
      [t('compare.proteinG'), "protein"],
      [t('compare.vitaminDMcg'), "vitaminD"],
      [t('compare.calciumMg'), "calcium"],
      [t('compare.ironMg'), "iron"],
      [t('compare.potassiumMg'), "potassium"],
    ];
    fdaRowDefs.forEach(([label, nutrientKey]) => {
      addRow(label, (c) => {
        const ss = servingSizes[c.code] ?? c.formula.serving_size_g;
        const perServing = scaleToServing(
          (c.nutrients as any)[nutrientKey],
          ss
        );
        const rounder = (fdaRound as any)[nutrientKey];
        if (!rounder) return num(perServing);
        const result = rounder(perServing);
        return typeof result === "number" ? result : String(result);
      });
    });

    // ─── K: %DV and Claims
    addSection(t('compare.sectionK'));
    addRow(t('compare.proteinDV'), (c) => {
      const ss = servingSizes[c.code] ?? c.formula.serving_size_g;
      const corrected = scaleToServing(c.pdcaas.correctedProteinPer100g, ss);
      return num((corrected / DRV.protein) * 100);
    });
    addRow(t('compare.fiberDV'), (c) => {
      const ss = servingSizes[c.code] ?? c.formula.serving_size_g;
      const fiberPS = scaleToServing(c.nutrients.fiber, ss);
      return num((fiberPS / DRV.fiber) * 100);
    });
    addRow(t('compare.proteinClaim'), (c) => {
      const ss = servingSizes[c.code] ?? c.formula.serving_size_g;
      const corrected = scaleToServing(c.pdcaas.correctedProteinPer100g, ss);
      return getProteinClaim((corrected / DRV.protein) * 100).claim;
    });
    addRow(t('compare.fiberClaim'), (c) => {
      const ss = servingSizes[c.code] ?? c.formula.serving_size_g;
      const fiberPS = scaleToServing(c.nutrients.fiber, ss);
      const pctDV = (fiberPS / DRV.fiber) * 100;
      if (pctDV >= 20) return t('claims.excellentSource');
      if (pctDV >= 10) return t('claims.goodSource');
      return t('claims.noClaim');
    });

    // ─── L: Custom Fields
    if (freeFields.length > 0) {
      addSection(t('compare.sectionL'));
      for (const ff of freeFields) {
        rows.push({
          type: "data",
          label: ff.label || t('compare.unnamedField'),
          values: computed.map((c) => ff.values[c.code] || ""),
        });
      }
    }

    // ─── Build Excel workbook
    const wb = XLSX.utils.book_new();

    // Build array of arrays for the sheet
    const headers = ["", ...computed.map((c) => `${c.code} - ${c.displayName}`)];
    const aoa: (string | number)[][] = [headers];
    const sectionRows: number[] = []; // track which rows are section headers (0-indexed in aoa)

    for (const row of rows) {
      if (row.type === "section") {
        sectionRows.push(aoa.length);
        aoa.push([row.title, ...Array(computed.length).fill("")]);
      } else {
        aoa.push([row.label, ...row.values]);
      }
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Column widths: label col wide, formula cols medium
    ws["!cols"] = [
      { wch: 38 },
      ...computed.map(() => ({ wch: 22 })),
    ];

    // Wrap text for ingredient rows (find rows with newlines)
    for (let r = 0; r < aoa.length; r++) {
      for (let c = 0; c < numCols; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        const cell = ws[cellRef];
        if (!cell) continue;
        if (!cell.s) cell.s = {};
        // Section header rows: bold
        if (sectionRows.includes(r)) {
          cell.s = { font: { bold: true } };
        }
        // Header row: bold
        if (r === 0) {
          cell.s = { font: { bold: true } };
        }
        // Wrap text if cell contains newlines
        if (typeof cell.v === "string" && cell.v.includes("\n")) {
          cell.s = { ...cell.s, alignment: { wrapText: true, vertical: "top" } };
        }
      }
    }

    // Merge section header cells across all formula columns
    ws["!merges"] = sectionRows.map((r) => ({
      s: { r, c: 0 },
      e: { r, c: numCols - 1 },
    }));

    XLSX.utils.book_append_sheet(wb, ws, "Formula Compare");

    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `formula_compare_${date}.xlsx`);
  }, [computed, loaded, servingSizes, freeFields, t, nameToKorean, i18n.language]); // eslint-disable-line

  /* ── Table row helper ─────────────────────────────────────────────── */

  // Position-based counter for unique <tr> keys. Resets every render
  // because it's redeclared on each FormulaCompare call. Previously the
  // <tr key={label}> approach broke when the same nutrient label (e.g.
  // "Protein (g)") was rendered in BOTH per-100g and per-serving sections
  // — React saw duplicate keys, fell back to index-based reconciliation,
  // and on language change the OLD-language rows persisted while NEW-
  // language rows were appended, growing the table by ~12 rows per toggle.
  let _trKeyCounter = 0;

  function tableRow(
    label: string,
    getValue: (c: ComputedFormula, idx: number) => React.ReactNode,
    opts?: { labelStyle?: React.CSSProperties }
  ) {
    const k = `tr-${_trKeyCounter++}`;
    return (
      <tr key={k}>
        <td style={{ ...labelCell, ...(opts?.labelStyle || {}) }}>{label}</td>
        {computed.map((c, i) => (
          <td key={c.code + i} style={dataCell}>
            {getValue(c, i)}
          </td>
        ))}
      </tr>
    );
  }

  function sectionHeader(title: string) {
    const k = `sec-${_trKeyCounter++}`;
    return (
      <tr key={k}>
        <td
          style={sectionHeaderStyle}
          colSpan={1 + computed.length}
        >
          {title}
        </td>
      </tr>
    );
  }

  /* ── FDA rounding helpers for per-serving display ─────────────────── */

  function fdaVal(
    nutrient: string,
    per100g: number,
    servingG: number
  ): string {
    const perServing = scaleToServing(per100g, servingG);
    const rounder = (fdaRound as any)[nutrient];
    if (!rounder) return fmt(perServing);
    const result = rounder(perServing);
    return fmtFda(result);
  }

  /* ── Chart renderer ───────────────────────────────────────────────── */

  const CHART_PALETTE = ["#8b5cf6", "#f59e0b", "#22c55e", "#ef4444", "#06b6d4", "#f472b6", "#84cc16", "#e879f9", "#fb923c", "#2dd4bf"];

  function renderChart(spec: ChartSpec, idx: number): React.ReactNode {
    const colors = spec.config?.colors || CHART_PALETTE;

    switch (spec.type) {
      case "radar": {
        // Auto-detect formula keys from data if config.formulaKeys is missing
        const keys: string[] = spec.config?.formulaKeys || (
          spec.data.length > 0
            ? Object.keys(spec.data[0]).filter(k => k !== "metric" && k !== "fullMark")
            : []
        );
        // Determine domain max from data (AI may not normalize to 100)
        let radarMax = 100;
        for (const d of spec.data) {
          for (const k of keys) {
            if (typeof d[k] === "number" && d[k] > radarMax) radarMax = d[k];
          }
        }
        // Round up to a nice number
        if (radarMax > 100) radarMax = Math.ceil(radarMax / 10) * 10;
        return (
          <ResponsiveContainer width="100%" height={380}>
            <RadarChart data={spec.data} cx="50%" cy="50%" outerRadius="70%">
              <PolarGrid stroke="rgba(139,92,246,0.2)" />
              <PolarAngleAxis
                dataKey="metric"
                tick={{ fill: "#94a3b8", fontSize: 10 }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, radarMax]}
                tick={{ fill: "#64748b", fontSize: 9 }}
                tickCount={5}
              />
              {keys.map((key, i) => (
                <Radar
                  key={key}
                  name={key}
                  dataKey={key}
                  stroke={colors[i % colors.length]}
                  fill={colors[i % colors.length]}
                  fillOpacity={0.15}
                />
              ))}
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              <Tooltip
                contentStyle={{
                  background: "#1e1b4b",
                  border: "1px solid rgba(139,92,246,0.3)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        );
      }

      case "scatter":
      case "bubble": {
        // Convert scatter/bubble to grouped bar (scatter is banned)
        const barData = spec.data.map((d: any) => ({
          name: d.formula || d.name || "?",
          [spec.config?.xLabel || "X"]: d.x ?? 0,
          [spec.config?.yLabel || "Y"]: d.y ?? 0,
        }));
        const xKey = spec.config?.xLabel || "X";
        const yKey = spec.config?.yLabel || "Y";
        return (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={barData} margin={{ top: 10, right: 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(139,92,246,0.15)" />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: "#1e1b4b",
                  border: "1px solid rgba(139,92,246,0.3)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              <Bar dataKey={xKey} fill={colors[0]} radius={[4, 4, 0, 0]} />
              <Bar dataKey={yKey} fill={colors[1]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case "bar":
      case "horizontalBar": {
        const isHoriz = spec.type === "horizontalBar";
        const color = spec.config?.color || colors[0];
        return (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={spec.data}
              layout={isHoriz ? "vertical" : "horizontal"}
              margin={{ top: 10, right: 30, bottom: 20, left: isHoriz ? 80 : 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(139,92,246,0.15)" />
              {isHoriz ? (
                <>
                  <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" tick={{ fill: "#94a3b8", fontSize: 11 }} width={70} />
                </>
              ) : (
                <>
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                </>
              )}
              <Tooltip
                contentStyle={{
                  background: "#1e1b4b",
                  border: "1px solid rgba(139,92,246,0.3)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case "groupedBar": {
        // Auto-detect metrics from data keys if config.metrics is missing
        const metricsDef: { key: string; label: string; color: string }[] =
          spec.config?.metrics || (
            spec.data.length > 0
              ? Object.keys(spec.data[0])
                  .filter(k => k !== "name")
                  .map((k, i) => ({ key: k, label: k, color: colors[i % colors.length] }))
              : []
          );
        return (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={spec.data} margin={{ top: 10, right: 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(139,92,246,0.15)" />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: "#1e1b4b",
                  border: "1px solid rgba(139,92,246,0.3)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              {metricsDef.map((m) => (
                <Bar
                  key={m.key}
                  dataKey={m.key}
                  name={m.label}
                  fill={m.color}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case "stackedBar": {
        // Auto-detect segments from data keys if config.segments is missing
        const segments: { key: string; label: string; color: string }[] =
          spec.config?.segments || (
            spec.data.length > 0
              ? Object.keys(spec.data[0])
                  .filter(k => k !== "name")
                  .map((k, i) => ({ key: k, label: k, color: colors[i % colors.length] }))
              : []
          );
        return (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={spec.data} margin={{ top: 10, right: 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(139,92,246,0.15)" />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: "#1e1b4b",
                  border: "1px solid rgba(139,92,246,0.3)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              {segments.map((s) => (
                <Bar
                  key={s.key}
                  dataKey={s.key}
                  name={s.label}
                  fill={s.color}
                  stackId="stack"
                  radius={[0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case "line": {
        // Auto-detect formula keys from data if config.formulaKeys is missing
        const keys: string[] = spec.config?.formulaKeys || (
          spec.data.length > 0
            ? Object.keys(spec.data[0]).filter(k => k !== "category")
            : []
        );
        return (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={spec.data} margin={{ top: 10, right: 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(139,92,246,0.15)" />
              <XAxis dataKey="category" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: "#1e1b4b",
                  border: "1px solid rgba(139,92,246,0.3)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              {keys.map((key, i) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={colors[i % colors.length]}
                  strokeWidth={2}
                  dot={{ r: 5, fill: colors[i % colors.length] }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      }

      case "multi_bar":
        // Treat multi_bar same as groupedBar
        return renderChart({ ...spec, type: "groupedBar" }, idx);

      case "composed": {
        // Auto-detect bars/lines from data keys if config is missing
        let bars: { key: string; label: string; color: string }[] =
          spec.config?.bars || [];
        let lines: { key: string; label: string; color: string }[] =
          spec.config?.lines || [];
        if (bars.length === 0 && lines.length === 0 && spec.data.length > 0) {
          // Fallback: treat all non-name keys as bars
          const dataKeys = Object.keys(spec.data[0]).filter(k => k !== "name");
          bars = dataKeys.map((k, i) => ({ key: k, label: k, color: colors[i % colors.length] }));
        }
        // Dual Y-axis: bars on left, lines on right (different scales)
        const useDualAxis = spec.config?.dualAxis !== false && lines.length > 0;
        const leftLabel = spec.config?.leftAxisLabel || "";
        const rightLabel = spec.config?.rightAxisLabel || "";
        return (
          <ResponsiveContainer width="100%" height={380}>
            <ComposedChart data={spec.data} margin={{ top: 10, right: useDualAxis ? 60 : 30, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(139,92,246,0.15)" />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis
                yAxisId="left"
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                label={leftLabel ? {
                  value: leftLabel,
                  angle: -90,
                  position: "insideLeft",
                  fill: "#94a3b8",
                  fontSize: 10,
                } : undefined}
              />
              {useDualAxis && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: "#94a3b8", fontSize: 11 }}
                  label={rightLabel ? {
                    value: rightLabel,
                    angle: 90,
                    position: "insideRight",
                    fill: "#94a3b8",
                    fontSize: 10,
                  } : undefined}
                />
              )}
              <Tooltip
                contentStyle={{
                  background: "#1e1b4b",
                  border: "1px solid rgba(139,92,246,0.3)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              {bars.map((b) => (
                <Bar
                  key={b.key}
                  dataKey={b.key}
                  name={b.label}
                  fill={b.color}
                  radius={[4, 4, 0, 0]}
                  yAxisId="left"
                />
              ))}
              {lines.map((l) => (
                <Line
                  key={l.key}
                  type="monotone"
                  dataKey={l.key}
                  name={l.label}
                  stroke={l.color}
                  strokeWidth={2}
                  dot={{ r: 4, fill: l.color }}
                  yAxisId={useDualAxis ? "right" : "left"}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        );
      }

      default:
        return (
          <div style={{ color: "#94a3b8", padding: "20px", textAlign: "center" }}>
            {t('compare.unsupportedChartType')} {spec.type}
          </div>
        );
    }
  }

  /* ── Render ───────────────────────────────────────────────────────── */

  if (initialLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          fontFamily: FONT_STACK,
          background: "#0a0618",
          color: "#a78bfa",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {t('compare.loadingFormulas')}
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily: FONT_STACK,
        background: "#0a0618",
        color: "#f8fafc",
        padding: "24px",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          maxWidth: "1600px",
          margin: "0 auto 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          background: "rgba(15, 11, 42, 0.85)",
          borderRadius: "12px",
          border: "1px solid rgba(139,92,246,0.2)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <h1
            style={{
              fontSize: "20px",
              fontWeight: 700,
              margin: 0,
              background: "linear-gradient(135deg, #c4b5fd, #f0abfc)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {t('compare.formulaCompare')}
          </h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <button
            onClick={downloadExcel}
            disabled={computed.length === 0}
            style={{
              padding: "6px 14px",
              borderRadius: "6px",
              border: "1px solid rgba(139,92,246,0.3)",
              background: computed.length > 0 ? "rgba(139,92,246,0.15)" : "transparent",
              color: computed.length > 0 ? "#c4b5fd" : "#475569",
              fontSize: "13px",
              cursor: computed.length > 0 ? "pointer" : "not-allowed",
              fontWeight: 500,
            }}
          >
            {t('compare.downloadExcel')}
          </button>
        </div>
      </div>

      {/* Formula selector slots */}
      <div
        style={{
          maxWidth: "1600px",
          margin: "0 auto 24px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          padding: "16px 20px",
          background: "rgba(15, 11, 42, 0.6)",
          borderRadius: "12px",
          border: "1px solid rgba(139,92,246,0.15)",
        }}
      >
        <span
          style={{
            fontSize: "12px",
            color: "#94a3b8",
            textTransform: "uppercase",
            letterSpacing: "1px",
            fontWeight: 600,
            marginRight: "4px",
          }}
        >
          {t('compare.formulasLabel')}
        </span>

        {slots.map((code, idx) => (
          <div
            key={idx}
            style={{ display: "flex", alignItems: "center", gap: "4px" }}
          >
            <select
              value={code}
              onChange={(e) => handleSlotChange(idx, e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: "6px",
                border: "1px solid rgba(139,92,246,0.3)",
                background: "#0f0b2a",
                color: code ? "#f8fafc" : "#64748b",
                fontSize: "13px",
                minWidth: "180px",
                cursor: "pointer",
              }}
            >
              <option value="">{t('compare.selectOption')}</option>
              {formulaList.map((f) => (
                <option
                  key={f.formula_code}
                  value={f.formula_code}
                  disabled={slots.some((s, i) => i !== idx && s === f.formula_code)}
                >
                  {f.formula_code} - {f.display_name}
                </option>
              ))}
            </select>
            {loadingSlots[code] && (
              <span style={{ fontSize: "11px", color: "#a78bfa" }}>...</span>
            )}
            {!loadingSlots[code] && slotErrors[code] && (
              <span
                style={{ fontSize: "11px", color: "#f87171" }}
                title={slotErrors[code]}
              >
                {t('compare.loadFailed')}
              </span>
            )}
            {slots.length > 1 && (
              <button
                onClick={() => handleRemoveSlot(idx)}
                style={{
                  padding: "2px 6px",
                  borderRadius: "4px",
                  border: "1px solid rgba(239,68,68,0.3)",
                  background: "transparent",
                  color: "#f87171",
                  fontSize: "12px",
                  cursor: "pointer",
                  lineHeight: 1,
                }}
                title={t('compare.removeSlot')}
              >
                x
              </button>
            )}
          </div>
        ))}

        {slots.length < MAX_SLOTS && (
          <button
            onClick={handleAddSlot}
            style={{
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid rgba(139,92,246,0.3)",
              background: "transparent",
              color: "#a78bfa",
              fontSize: "13px",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {t('compare.addFormula')}
          </button>
        )}
      </div>

      {/* Main area */}
      <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
        {computed.length === 0 ? (
          <div
            style={{
              padding: "60px 20px",
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "15px",
            }}
          >
            {t('compare.selectFormulasToCompare')}
          </div>
        ) : (
          <div
            style={{
              overflowX: "auto",
              borderRadius: "12px",
              border: "1px solid rgba(139,92,246,0.2)",
              background: "rgba(15, 11, 42, 0.6)",
            }}
          >
            <table
              style={{
                borderCollapse: "collapse",
                width: "max-content",
                minWidth: "100%",
              }}
            >
              {/* Column header row */}
              <thead>
                <tr>
                  <th
                    style={{
                      ...labelCell,
                      fontWeight: 700,
                      borderBottom:
                        "2px solid rgba(139,92,246,0.3)",
                      top: 0,
                      zIndex: 3,
                      background: "#0f0b2a",
                    }}
                  >
                    &nbsp;
                  </th>
                  {computed.map((c, i) => (
                    <th
                      key={c.code + i}
                      style={{
                        ...dataCell,
                        fontWeight: 700,
                        color: "#c4b5fd",
                        borderBottom:
                          "2px solid rgba(139,92,246,0.3)",
                        top: 0,
                        zIndex: 1,
                        background: "#0f0b2a",
                      }}
                    >
                      {c.code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* ═══ Section A: Formula Identity ═══ */}
                {sectionHeader(t('compare.sectionA'))}
                {tableRow(t('compare.formulaCode'), (c) => c.code)}
                {tableRow(t('compare.displayName'), (c) => (
                  <span style={{ whiteSpace: "normal", textAlign: "right", display: "block" }}>
                    {c.displayName}
                  </span>
                ))}
                {tableRow(t('compare.components'), (c) =>
                  c.formula.single_component || Object.keys(loaded[c.code]?.fillingFormula || {}).length === 0
                    ? t('compare.single')
                    : t('compare.doughPlusFilling')
                )}
                {tableRow(t('compare.ratioMode'), (c) =>
                  c.formula.ratio_mode === "finished"
                    ? t('compare.postProcessLabel')
                    : t('compare.asMixedLabel')
                )}
                {tableRow(t('compare.doughRatioPct'), (c) =>
                  fmt(c.effectiveDoughRatio, 1)
                )}
                {tableRow(t('compare.fillingRatioPct'), (c) =>
                  fmt(c.effectiveFillingRatio, 1)
                )}
                {tableRow(t('compare.postProcess'), (c) => {
                  if (!c.isPostProcessed) return t('common.no');
                  const mode = c.formula.final_moisture_mode;
                  if (mode === "composite")
                    return `${t('common.yes')} - ${t('compare.compositeLabel')} ${fmt(c.formula.final_moisture_whole ?? 0, 1)}%`;
                  if (mode === "components")
                    return `${t('common.yes')} - ${t('formulation.doughLabel')} ${fmt(c.formula.final_moisture_dough ?? 0, 1)}% / ${t('formulation.fillingLabel')} ${fmt(c.formula.final_moisture_filling ?? 0, 1)}%`;
                  return t('common.yes');
                })}

                {/* ═══ Section B: Per 100g - Moisture & Macronutrients ═══ */}
                {sectionHeader(t('compare.sectionB'))}
                {tableRow(t('compare.moisture'), (c) => fmt(c.nutrients.moisture))}
                {tableRow(t('compare.proteinG'), (c) => fmt(c.nutrients.protein))}
                {tableRow(t('compare.totalFatG'), (c) => fmt(c.nutrients.fat))}
                {tableRow(t('compare.saturatedFatG'), (c) => fmt(c.nutrients.satfat))}
                {tableRow(t('compare.transFatG'), (c) => fmt(c.nutrients.transfat))}
                {tableRow(t('compare.cholesterolMg'), (c) =>
                  fmt(c.nutrients.cholesterol)
                )}
                {tableRow(t('compare.totalCarbG'), (c) =>
                  fmt(c.nutrients.carbs)
                )}
                {tableRow(t('compare.totalDietaryFiberG'), (c) =>
                  fmt(c.nutrients.fiber)
                )}
                {tableRow(t('compare.totalSugarsG'), (c) => fmt(c.nutrients.sugar))}
                {tableRow(t('compare.sodiumMg'), (c) => fmt(c.nutrients.sodium))}
                {tableRow(t('compare.caloriesKcal'), (c) =>
                  fmt(c.nutrients.calories)
                )}

                {/* ═══ Section C: Per 100g - Micronutrients ═══ */}
                {sectionHeader(t('compare.sectionC'))}
                {tableRow(t('compare.vitaminDMcg'), (c) =>
                  fmt(c.nutrients.vitaminD)
                )}
                {tableRow(t('compare.calciumMg'), (c) => fmt(c.nutrients.calcium))}
                {tableRow(t('compare.ironMg'), (c) => fmt(c.nutrients.iron))}
                {tableRow(t('compare.potassiumMg'), (c) => fmt(c.nutrients.potassium))}

                {/* ═══ Section D: Per 100g - Fiber Fractions ═══ */}
                {sectionHeader(t('compare.sectionD'))}
                {tableRow(t('compare.insolubleFiberG'), (c) =>
                  fmt(c.nutrients.fiberInsoluble)
                )}
                {tableRow(t('compare.solubleFiberG'), (c) =>
                  fmt(c.nutrients.fiberSoluble)
                )}
                {tableRow(t('compare.resistantStarchG'), (c) =>
                  fmt(c.nutrients.resistantStarch)
                )}

                {/* ═══ Section E: Per 100g - Fatty Acid Profile ═══ */}
                {sectionHeader(t('compare.sectionE'))}
                {tableRow(t('compare.ala'), (c) => fmt(c.nutrients.faAla))}
                {tableRow(t('compare.la'), (c) => fmt(c.nutrients.faLa))}
                {tableRow(t('compare.oleic'), (c) => fmt(c.nutrients.faOleic))}
                {tableRow(t('compare.palmitic'), (c) =>
                  fmt(c.nutrients.faPalmitic)
                )}
                {tableRow(t('compare.stearic'), (c) =>
                  fmt(c.nutrients.faStearic)
                )}
                {tableRow(t('compare.n6n3Ratio'), (c) =>
                  c.nutrients.faAla > 0
                    ? fmt(c.nutrients.faLa / c.nutrients.faAla)
                    : t('compare.na')
                )}

                {/* ═══ Section F: Amino Acid Profile (mg/g protein) ═══ */}
                {sectionHeader(t('compare.sectionF'))}
                {Object.keys(REFERENCE_PATTERN).map((aa) =>
                  tableRow(
                    `${AA_NAMES[aa]} (${AA_FULL_NAMES[aa]})`,
                    (c) => {
                      const val = c.pdcaas.blendedAA[aa] ?? 0;
                      const isLimiting = c.pdcaas.limitingAA === aa;
                      return (
                        <span
                          style={
                            isLimiting ? { color: "#ef4444" } : undefined
                          }
                        >
                          {fmt(val)}
                        </span>
                      );
                    }
                  )
                )}
                {tableRow(t('compare.bcaa'), (c) => {
                  const b =
                    (c.pdcaas.blendedAA.leucine ?? 0) +
                    (c.pdcaas.blendedAA.isoleucine ?? 0) +
                    (c.pdcaas.blendedAA.valine ?? 0);
                  return fmt(b);
                })}

                {/* ═══ Section G: Protein Quality (PDCAAS) ═══ */}
                {sectionHeader(t('compare.sectionG'))}
                {tableRow(t('compare.weightedDigestibility'), (c) =>
                  fmt(c.pdcaas.weightedDigestibility, 4)
                )}
                {tableRow(t('compare.limitingAminoAcid'), (c) => (
                  <span style={{ color: "#ef4444" }}>
                    {c.pdcaas.limitingAA
                      ? AA_FULL_NAMES[c.pdcaas.limitingAA] ||
                        c.pdcaas.limitingAA
                      : "—"}
                  </span>
                ))}
                {tableRow(t('compare.aminoAcidScorePct'), (c) =>
                  fmt(c.pdcaas.aminoAcidScore * 100, 1)
                )}
                {tableRow(t('compare.pdcaasLabel'), (c) => (
                  <span
                    style={{
                      color: pdcaasColor(c.pdcaas.truncatedPdcaas),
                      fontWeight: 700,
                    }}
                  >
                    {fmt(c.pdcaas.truncatedPdcaas, 4)}
                  </span>
                ))}
                {tableRow(t('compare.correctedProteinG100g'), (c) =>
                  fmt(c.pdcaas.correctedProteinPer100g)
                )}

                {/* ═══ Section H: Ingredient List ═══ */}
                {sectionHeader(t('compare.sectionH'))}
                {tableRow(
                  computed.some((c) => c.isPostProcessed)
                    ? t('compare.ingredientsPostProcess')
                    : t('compare.ingredients'),
                  (c) => (
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        textAlign: "left",
                        fontSize: "11px",
                        lineHeight: 1.5,
                        maxWidth: COL_WIDTH,
                      }}
                    >
                      {c.ingredientList.map((ing) => (
                        <div key={ing.name}>
                          {ingName(ing.name)} ({fmt(ing.pct, 1)}%)
                        </div>
                      ))}
                    </div>
                  )
                )}

                {/* ═══ Section I: Per Serving - Parameters ═══ */}
                {sectionHeader(t('compare.sectionI'))}
                {tableRow(t('compare.servingSizeG'), (c) => (
                  <input
                    type="number"
                    value={servingSizes[c.code] ?? c.formula.serving_size_g}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val) && val > 0) {
                        setServingSizes((prev) => ({
                          ...prev,
                          [c.code]: val,
                        }));
                      }
                    }}
                    style={{
                      width: "70px",
                      padding: "3px 6px",
                      borderRadius: "4px",
                      border: "1px solid rgba(139,92,246,0.3)",
                      background: "#0f0b2a",
                      color: "#f8fafc",
                      fontSize: "13px",
                      textAlign: "right",
                    }}
                  />
                ))}
                {tableRow(t('compare.piecesPerServing'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  const pw = c.formula.piece_weight_g;
                  return pw > 0 ? (ss / pw).toFixed(1) : "—";
                })}

                {/* ═══ Section J: Per Serving - FDA Nutrition Facts ═══ */}
                {sectionHeader(t('compare.sectionJ'))}
                {tableRow(t('compare.calories'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("calories", c.nutrients.calories, ss);
                })}
                {tableRow(t('compare.totalFatG'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("fat", c.nutrients.fat, ss);
                })}
                {tableRow(t('compare.saturatedFatG'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("satfat", c.nutrients.satfat, ss);
                })}
                {tableRow(t('compare.transFatG'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("transfat", c.nutrients.transfat, ss);
                })}
                {tableRow(t('compare.cholesterolMg'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("cholesterol", c.nutrients.cholesterol, ss);
                })}
                {tableRow(t('compare.sodiumMg'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("sodium", c.nutrients.sodium, ss);
                })}
                {tableRow(t('compare.totalCarbG'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("carbs", c.nutrients.carbs, ss);
                })}
                {tableRow(t('compare.dietaryFiberG'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("fiber", c.nutrients.fiber, ss);
                })}
                {tableRow(t('compare.totalSugarsG'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("sugar", c.nutrients.sugar, ss);
                })}
                {tableRow(t('compare.proteinG'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("protein", c.nutrients.protein, ss);
                })}
                {tableRow(t('compare.vitaminDMcg'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("vitaminD", c.nutrients.vitaminD, ss);
                })}
                {tableRow(t('compare.calciumMg'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("calcium", c.nutrients.calcium, ss);
                })}
                {tableRow(t('compare.ironMg'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("iron", c.nutrients.iron, ss);
                })}
                {tableRow(t('compare.potassiumMg'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  return fdaVal("potassium", c.nutrients.potassium, ss);
                })}

                {/* ═══ Section K: Per Serving - %DV and Claims ═══ */}
                {sectionHeader(t('compare.sectionK'))}
                {tableRow(t('compare.proteinDV'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  const correctedPerServing = scaleToServing(
                    c.pdcaas.correctedProteinPer100g,
                    ss
                  );
                  const pctDV = (correctedPerServing / DRV.protein) * 100;
                  return fmt(pctDV, 1) + "%";
                })}
                {tableRow(t('compare.fiberDV'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  const fiberPerServing = scaleToServing(
                    c.nutrients.fiber,
                    ss
                  );
                  const pctDV = (fiberPerServing / DRV.fiber) * 100;
                  return fmt(pctDV, 1) + "%";
                })}
                {tableRow(t('compare.proteinClaim'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  const correctedPerServing = scaleToServing(
                    c.pdcaas.correctedProteinPer100g,
                    ss
                  );
                  const pctDV = (correctedPerServing / DRV.protein) * 100;
                  const { claim } = getProteinClaim(pctDV);
                  return (
                    <span
                      style={{
                        color: claimColor(claim),
                        fontWeight: 600,
                      }}
                    >
                      {claimDisplay[claim] || claim}
                    </span>
                  );
                })}
                {tableRow(t('compare.fiberClaim'), (c) => {
                  const ss =
                    servingSizes[c.code] ?? c.formula.serving_size_g;
                  const fiberPerServing = scaleToServing(
                    c.nutrients.fiber,
                    ss
                  );
                  const pctDV = (fiberPerServing / DRV.fiber) * 100;
                  let claimKey: string;
                  let color: string;
                  if (pctDV >= 20) {
                    claimKey = "Excellent Source";
                    color = "#22c55e";
                  } else if (pctDV >= 10) {
                    claimKey = "Good Source";
                    color = "#f59e0b";
                  } else {
                    claimKey = "No Claim";
                    color = "#64748b";
                  }
                  return (
                    <span style={{ color, fontWeight: 600 }}>{claimDisplay[claimKey] || claimKey}</span>
                  );
                })}

                {/* ═══ Section L: Custom Fields ═══ */}
                {sectionHeader(t('compare.sectionL'))}
                {freeFields.map((ff) => (
                  <tr key={ff.id}>
                    <td style={{ ...labelCell, position: "relative" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <input
                          type="text"
                          value={ff.label}
                          onChange={(e) => handleFreeFieldLabelChange(ff.id, e.target.value)}
                          placeholder={t('compare.enterFieldName')}
                          style={{
                            width: "100%",
                            padding: "3px 6px",
                            borderRadius: "4px",
                            border: "1px solid rgba(139,92,246,0.3)",
                            background: "#0f0b2a",
                            color: "#f8fafc",
                            fontSize: "13px",
                            textAlign: "left",
                          }}
                        />
                        <button
                          onClick={() => handleRemoveFreeField(ff.id)}
                          style={{
                            padding: "1px 5px",
                            borderRadius: "3px",
                            border: "1px solid rgba(239,68,68,0.3)",
                            background: "transparent",
                            color: "#f87171",
                            fontSize: "11px",
                            cursor: "pointer",
                            lineHeight: 1,
                            opacity: 0.6,
                            flexShrink: 0,
                          }}
                          title={t('compare.removeField')}
                        >
                          x
                        </button>
                      </div>
                    </td>
                    {computed.map((c) => (
                      <td key={c.code} style={dataCell}>
                        <input
                          type="text"
                          value={ff.values[c.code] || ""}
                          onChange={(e) => handleFreeFieldValueChange(ff.id, c.code, e.target.value)}
                          placeholder="—"
                          style={{
                            width: "100%",
                            padding: "3px 6px",
                            borderRadius: "4px",
                            border: "1px solid rgba(139,92,246,0.3)",
                            background: "#0f0b2a",
                            color: "#f8fafc",
                            fontSize: "13px",
                            textAlign: "right",
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Add free field button row */}
                <tr>
                  <td style={{ ...labelCell }}>
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <button
                        onClick={handleAddFreeField}
                        className="free-field-add-btn"
                        style={{
                          padding: "4px 10px",
                          borderRadius: "5px",
                          border: "1px solid rgba(139,92,246,0.3)",
                          background: "transparent",
                          color: "#a78bfa",
                          fontSize: "12px",
                          cursor: "pointer",
                          fontWeight: 500,
                        }}
                      >
                        {t('compare.addField')}
                      </button>
                      <div
                        className="free-field-tooltip"
                        style={{
                          position: "absolute",
                          bottom: "calc(100% + 8px)",
                          left: 0,
                          width: "280px",
                          padding: "10px 12px",
                          background: "#1e1b4b",
                          border: "1px solid rgba(139,92,246,0.4)",
                          borderRadius: "8px",
                          fontSize: "11px",
                          color: "#cbd5e1",
                          lineHeight: 1.5,
                          zIndex: 10,
                          pointerEvents: "none",
                          opacity: 0,
                          transition: "opacity 0.2s",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                        }}
                      >
                        {t('compare.addFieldTooltip')}
                        <br /><br />
                        {t('compare.chartsConsiderFields')}
                      </div>
                      <style>{`.free-field-add-btn:hover + .free-field-tooltip { opacity: 1 !important; }`}</style>
                    </div>
                  </td>
                  <td colSpan={computed.length} style={{ ...dataCell, textAlign: "left", color: "#64748b", fontSize: "11px" }}>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* ── Formula Performance Dashboard (Column-Aligned Grid) ── */}
        {computed.length >= 1 && (
          <div style={{
            marginTop: "32px",
            animation: "gaugeSlideUp 0.5s ease-out both",
          }}>
            <h2 style={{
              fontSize: "16px",
              fontWeight: 700,
              margin: "0 0 20px",
              background: "linear-gradient(135deg, #c4b5fd, #67e8f9)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "0.5px",
            }}>
              {t('compare.formulaPerformanceDashboard')}
            </h2>

            <div style={{
              overflowX: computed.length > 5 ? "auto" : undefined,
              background: "rgba(15, 11, 42, 0.7)",
              border: "1px solid rgba(139,92,246,0.2)",
              borderRadius: "16px",
              padding: "24px",
              animation: "dashboardGlow 4s ease-in-out infinite",
              backdropFilter: "blur(8px)",
            }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: `repeat(${computed.length}, minmax(180px, 1fr))`,
                gap: "16px",
                alignItems: "start",
              }}>
                {/* Header row */}
                {computed.map((c, i) => (
                  <div key={c.code} style={{
                    textAlign: "center",
                    borderBottom: "2px solid rgba(139,92,246,0.2)",
                    paddingBottom: "8px",
                  }}>
                    <div style={{
                      width: "8px", height: "8px", borderRadius: "50%",
                      background: CHART_PALETTE[i % CHART_PALETTE.length],
                      boxShadow: `0 0 8px ${CHART_PALETTE[i % CHART_PALETTE.length]}`,
                      margin: "0 auto 4px",
                    }} />
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#f8fafc" }}>{c.code}</div>
                    <div style={{ fontSize: "10px", color: "#64748b" }}>{c.displayName}</div>
                  </div>
                ))}

                {/* Row label: vs Targets */}
                <div style={{
                  gridColumn: `1 / -1`,
                  fontSize: "11px", color: "#a78bfa", fontWeight: 600,
                  paddingTop: "16px", paddingBottom: "4px",
                  borderBottom: "1px solid rgba(139,92,246,0.1)",
                  cursor: "help",
                }} title={t('compare.vsTargetsTooltip')}>
                  {t('compare.vsTargets')}
                </div>

                {/* Row 1: Speedometers */}
                {computed.map((c, cIdx) => {
                  const ss = servingSizes[c.code] ?? c.formula.serving_size_g;
                  const proteinPerServing = scaleToServing(c.nutrients.protein, ss);
                  const fiberPerServing = scaleToServing(c.nutrients.fiber, ss);
                  return (
                    <div key={c.code} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                      <GaugeSpeedometer
                        value={proteinPerServing} target={27} maxVal={40}
                        label={t('formulation.protein')} unit="g" color="#8b5cf6"
                        size={135} animDelay={cIdx * 0.15} t={t}
                      />
                      <GaugeSpeedometer
                        value={fiberPerServing} target={10} maxVal={15}
                        label={t('formulation.fiber')} unit="g" color="#22c55e"
                        size={135} animDelay={cIdx * 0.15 + 0.1} t={t}
                      />
                    </div>
                  );
                })}

                {/* Row label: %DV */}
                <div style={{
                  gridColumn: `1 / -1`,
                  fontSize: "11px", color: "#a78bfa", fontWeight: 600,
                  paddingTop: "24px", paddingBottom: "4px",
                  borderBottom: "1px solid rgba(139,92,246,0.1)",
                  cursor: "help",
                }} title={t('compare.pctDailyValueTooltip')}>
                  {t('compare.pctDailyValue')}
                </div>

                {/* Row 2: %DV Rings */}
                {computed.map((c, cIdx) => {
                  const ss = servingSizes[c.code] ?? c.formula.serving_size_g;
                  const correctedProteinPerServing = scaleToServing(c.pdcaas.correctedProteinPer100g, ss);
                  const fiberPerServing = scaleToServing(c.nutrients.fiber, ss);
                  const fatPerServing = scaleToServing(c.nutrients.fat, ss);
                  const proteinDV = (correctedProteinPerServing / DRV.protein) * 100;
                  const fiberDV = (fiberPerServing / DRV.fiber) * 100;
                  const fatDV = (fatPerServing / DRV.fat) * 100;
                  return (
                    <div key={c.code} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <NestedDVGauge
                        proteinDV={proteinDV} fiberDV={fiberDV} fatDV={fatDV}
                        formulaCode={c.code} size={150} animDelay={cIdx * 0.15 + 0.2} t={t}
                      />
                      <div style={{ display: "flex", gap: "10px", marginTop: "2px" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", fontWeight: 700, color: "#8b5cf6" }}>{proteinDV.toFixed(0)}%</div>
                          <div style={{ fontSize: "8px", color: "#64748b" }}>{t('compare.protShort')}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", fontWeight: 700, color: "#22c55e" }}>{fiberDV.toFixed(0)}%</div>
                          <div style={{ fontSize: "8px", color: "#64748b" }}>{t('formulation.fiber')}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", fontWeight: 700, color: "#f59e0b" }}>{fatDV.toFixed(0)}%</div>
                          <div style={{ fontSize: "8px", color: "#64748b" }}>{t('formulation.fat')}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Row label: Nutrient Breakdown */}
                <div style={{
                  gridColumn: `1 / -1`,
                  fontSize: "11px", color: "#a78bfa", fontWeight: 600,
                  paddingTop: "24px", paddingBottom: "4px",
                  borderBottom: "1px solid rgba(139,92,246,0.1)",
                  cursor: "help",
                }} title={t('compare.nutrientBreakdownTooltip')}>
                  {t('compare.nutrientBreakdown')}
                </div>

                {/* Row 3: Nutrient Stacked Bar */}
                {computed.map((c, cIdx) => {
                  const stackBarFormulas = computed.map((cc) => {
                    const ssg = servingSizes[cc.code] ?? cc.formula.serving_size_g;
                    const prot = scaleToServing(cc.nutrients.protein, ssg);
                    const fib = scaleToServing(cc.nutrients.fiber, ssg);
                    const ironS = scaleToServing(cc.nutrients.iron, ssg);
                    const calcS = scaleToServing(cc.nutrients.calcium, ssg);
                    const potS = scaleToServing(cc.nutrients.potassium, ssg);
                    const microScore = (ironS / 18 * 100) + (calcS / 1300 * 100) + (potS / 4700 * 100);
                    const totalCal = scaleToServing(cc.nutrients.calories, ssg);
                    const otherCal = Math.max(totalCal - (prot * 4 + fib * 4), 0);
                    return { code: cc.code, protein: prot, fiber: fib, micro: microScore, other: otherCal / 4 };
                  });
                  return (
                    <div key={c.code} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <NutrientStackedBar
                        formulas={stackBarFormulas}
                        currentIndex={cIdx}
                        animDelay={cIdx * 0.15 + 0.3}
                        t={t}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── AI-Selected Comparative Charts (3 core) ────────────── */}
        {computed.length >= 2 && (
          <div style={{ marginTop: "32px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "20px",
              }}
            >
              <h2
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  margin: 0,
                  color: "#c4b5fd",
                }}
              >
                {t('compare.comparativeCharts')}
              </h2>
              {chartsLoading && (
                <span style={{ fontSize: "12px", color: "#a78bfa" }}>
                  {t('compare.generatingCharts')}
                </span>
              )}
              {chartsError && (
                <span style={{ fontSize: "12px", color: "#f59e0b" }}>
                  {chartsError}
                </span>
              )}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
                gap: "20px",
              }}
            >
              {chartSpecs.map((spec, idx) => (
                <div
                  key={spec.title + idx}
                  style={{
                    background: "rgba(15, 11, 42, 0.6)",
                    border: "1px solid rgba(139,92,246,0.2)",
                    borderRadius: "12px",
                    padding: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#f8fafc",
                      margin: "0 0 4px",
                    }}
                  >
                    {spec.title}
                  </h3>
                  <div style={{ marginTop: "12px" }}>
                    {renderChart(spec, idx)}
                  </div>
                  {spec.rationale && (
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#94a3b8",
                        margin: "8px 0 0",
                        lineHeight: 1.4,
                        fontStyle: "italic",
                      }}
                    >
                      {spec.rationale}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Column-Aligned AI Insights (Dropdowns) ──────────────── */}
        {computed.length >= 1 && (
          <div style={{
            marginTop: "32px",
            background: "rgba(15, 11, 42, 0.7)",
            border: "1px solid rgba(139,92,246,0.2)",
            borderRadius: "16px",
            padding: "24px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
              <h2 style={{
                fontSize: "16px",
                fontWeight: 700,
                margin: 0,
                background: "linear-gradient(135deg, #c4b5fd, #67e8f9)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>
                {t('compare.aiInsights')}
              </h2>
              <button
                onClick={() => {
                  // Generate all 4 sections for all formulas at once
                  for (const c of computed) {
                    generateBatchAccordionContent(c.code);
                  }
                }}
                disabled={Object.values(accordionContent).some(v => v?.loading)}
                style={{
                  padding: "6px 16px",
                  borderRadius: "6px",
                  border: "1px solid rgba(139,92,246,0.3)",
                  background: Object.values(accordionContent).some(v => v?.loading)
                    ? "transparent"
                    : "rgba(139,92,246,0.15)",
                  color: Object.values(accordionContent).some(v => v?.loading) ? "#64748b" : "#c4b5fd",
                  fontSize: "13px",
                  cursor: Object.values(accordionContent).some(v => v?.loading) ? "not-allowed" : "pointer",
                  fontWeight: 500,
                }}
              >
                {Object.values(accordionContent).some(v => v?.loading)
                  ? t('compare.generating')
                  : Object.values(accordionContent).some(v => v?.content)
                  ? t('compare.regenerateAll')
                  : t('compare.generateInsights')}
              </button>
            </div>

            {/* Dropdown selector for which insight to show */}
            {(["clinical", "claims", "nova", "vs_foods"] as const).map((section) => {
              const sectionLabels: Record<string, string> = {
                clinical: t('compare.clinicalPotential'),
                claims: t('compare.claimsRegulatory'),
                nova: t('compare.upfNova'),
                vs_foods: t('compare.vsCommonFoods'),
              };
              const hasAnyContent = computed.some(c => {
                const e = accordionContent[`${c.code}-${section}-${i18n.language}`];
                return !!(e?.content || e?.loading || e?.error);
              });
              const isCollapsed = !!accordionState["_global"]?.[section];

              return (
                <div key={section} style={{ marginBottom: "8px" }}>
                  <button
                    onClick={() => toggleAccordion("_global", section)}
                    style={{
                      width: "100%",
                      padding: "8px 16px",
                      background: isCollapsed ? "transparent" : "rgba(139,92,246,0.06)",
                      border: "none",
                      borderBottom: "1px solid rgba(139,92,246,0.1)",
                      color: "#c4b5fd",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      textAlign: "left",
                    }}
                  >
                    <span>{sectionLabels[section]}</span>
                    <span style={{ transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)", transition: "transform 0.2s", fontSize: "12px" }}>
                      {hasAnyContent ? "▸" : ""}
                    </span>
                  </button>

                  {!isCollapsed && hasAnyContent && (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${computed.length}, minmax(180px, 1fr))`,
                      gap: "12px",
                      padding: "12px 8px",
                    }}>
                      {computed.map((c) => {
                        const key = `${c.code}-${section}-${i18n.language}`;
                        const entry = accordionContent[key];
                        return (
                          <div key={c.code} style={{
                            background: "rgba(30, 20, 60, 0.8)",
                            borderRadius: "8px",
                            padding: "12px 14px",
                            border: "1px solid rgba(139,92,246,0.15)",
                          }}>
                            <div style={{ fontSize: "10px", color: "#a78bfa", fontWeight: 600, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                              {c.code}
                            </div>
                            {entry?.loading && (
                              <span style={{ color: "#a78bfa", fontSize: "12px" }}>{t('compare.generating')}</span>
                            )}
                            {entry?.error && (
                              <span style={{ color: "#f87171", fontSize: "12px" }}>{entry.error}</span>
                            )}
                            {entry?.content && (
                              <p style={{
                                color: "#cbd5e1",
                                fontSize: "12px",
                                lineHeight: 1.6,
                                margin: 0,
                              }}>
                                {entry.content}
                              </p>
                            )}
                            {!entry?.content && !entry?.loading && !entry?.error && (
                              <span style={{ color: "#475569", fontSize: "11px", fontStyle: "italic" }}>
                                {t('compare.clickGenerateInsights')}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── More Charts (Custom Criteria, only if free fields have data) ── */}
        {computed.length >= 2 && freeFields.some(ff => ff.label.trim() && Object.values(ff.values).some(v => v.trim())) && (
          <div style={{ marginTop: "24px" }}>
            <button
              onClick={generateFreeFieldCharts}
              disabled={freeFieldChartsLoading}
              style={{
                padding: "8px 20px",
                borderRadius: "8px",
                border: "1px solid rgba(139,92,246,0.3)",
                background: freeFieldChartsLoading ? "transparent" : "rgba(139,92,246,0.1)",
                color: freeFieldChartsLoading ? "#64748b" : "#c4b5fd",
                fontSize: "13px",
                cursor: freeFieldChartsLoading ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              {freeFieldChartsLoading ? t('compare.generating') : freeFieldChartSpecs.length > 0 ? t('compare.refreshCharts') : t('compare.moreCharts')}
            </button>
            {freeFieldChartSpecs.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
                gap: "20px",
                marginTop: "16px",
              }}>
                {freeFieldChartSpecs.map((spec, idx) => (
                  <div
                    key={spec.title + idx}
                    style={{
                      background: "rgba(15, 11, 42, 0.6)",
                      border: "1px solid rgba(139,92,246,0.2)",
                      borderRadius: "12px",
                      padding: "20px",
                    }}
                  >
                    <h3 style={{ fontSize: "14px", fontWeight: 600, color: "#f8fafc", margin: "0 0 4px" }}>
                      {spec.title}
                    </h3>
                    <div style={{ marginTop: "12px" }}>{renderChart(spec, idx + 100)}</div>
                    {spec.rationale && (
                      <p style={{ fontSize: "11px", color: "#94a3b8", margin: "8px 0 0", lineHeight: 1.4, fontStyle: "italic" }}>
                        {spec.rationale}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── AI Insights Report ─────────────────────────────────── */}
        {computed.length >= 1 && (
          <div style={{ marginTop: "32px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "20px",
              }}
            >
              <h2
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  margin: 0,
                  color: "#c4b5fd",
                }}
              >
                {t('compare.aiInsightsReport')}
              </h2>
              <button
                onClick={generateInsights}
                disabled={insightsLoading}
                style={{
                  padding: "6px 16px",
                  borderRadius: "6px",
                  border: "1px solid rgba(139,92,246,0.3)",
                  background: insightsLoading
                    ? "transparent"
                    : "rgba(139,92,246,0.15)",
                  color: insightsLoading ? "#64748b" : "#c4b5fd",
                  fontSize: "13px",
                  cursor: insightsLoading ? "not-allowed" : "pointer",
                  fontWeight: 500,
                }}
              >
                {insightsLoading
                  ? t('compare.generatingReport')
                  : insightsReport
                  ? t('compare.regenerateReport')
                  : t('compare.generateReport')}
              </button>
              {insightsLoading && (
                <span style={{ fontSize: "11px", color: "#a78bfa" }}>
                  {t('compare.searchingJournals')}
                </span>
              )}
            </div>

            {insightsError && (
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: "8px",
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  color: "#fca5a5",
                  fontSize: "13px",
                  marginBottom: "16px",
                }}
              >
                {insightsError}
              </div>
            )}

            {insightsReport && (
              <div
                style={{
                  background: "rgba(15, 11, 42, 0.6)",
                  border: "1px solid rgba(139,92,246,0.2)",
                  borderRadius: "12px",
                  padding: "28px 32px",
                  maxWidth: "900px",
                }}
              >
                {renderMarkdown(insightsReport)}

                {/* Sources */}
                {insightsSources.length > 0 && (
                  <div style={{ marginTop: "24px" }}>
                    <h4
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#a78bfa",
                        margin: "0 0 8px",
                        borderBottom: "1px solid rgba(139,92,246,0.2)",
                        paddingBottom: "4px",
                      }}
                    >
                      {t('compare.sourcesConsulted')}
                    </h4>
                    <ul
                      style={{
                        margin: 0,
                        padding: "0 0 0 16px",
                        listStyle: "decimal",
                      }}
                    >
                      {insightsSources.map((src, idx) => (
                        <li
                          key={idx}
                          style={{
                            color: "#94a3b8",
                            fontSize: "11px",
                            lineHeight: 1.6,
                            marginBottom: "2px",
                          }}
                        >
                          <a
                            href={src.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#8b5cf6",
                              textDecoration: "none",
                            }}
                          >
                            {src.title || src.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {!insightsReport && !insightsLoading && !insightsError && (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: "13px",
                  background: "rgba(15, 11, 42, 0.3)",
                  borderRadius: "12px",
                  border: "1px dashed rgba(139,92,246,0.15)",
                }}
              >
                {t('compare.clickGenerateReport')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
